/**
 * Every string the console renders. Same shape and same discipline as
 * `src/content/ar.ts`, so an English sibling later is "a translation file, not a
 * refactor" — the convention the README already sets for this repo.
 *
 * Two rules, both learned from the prototype:
 *
 * 1. **No inline Arabic in components.** The prototype was consistent about
 *    this on the client and abandoned it on the server, where notification
 *    bodies and SMS templates were hardcoded Arabic string literals in the
 *    controllers — so a user whose language was set to English still received
 *    Arabic. Server-generated text comes from here too.
 *
 * 2. **No orphan keys.** The prototype shipped about twenty translated strings
 *    that nothing rendered — `notifications.markAllRead`, `zones.selectZone`,
 *    `guards.createdSuccess`, most of `common.*` — each one a feature someone
 *    intended and never built. `scripts/check-content-keys.mjs` fails the build
 *    on a key no source file reads, so the catalogue cannot quietly become a
 *    wishlist again.
 *
 * ## What is deliberately NOT here
 *
 * Sixty-one strings that the API owns: every validation message, every failure
 * reason, and every success confirmation — `employees.created`,
 * `leave.errors.notPending`, `settings.errors.passwordTooShort`, and the rest.
 * They are generated where the decision is made, travel over the wire inside
 * `{ ok: false, error, fieldErrors }`, and are rendered here verbatim. This
 * console never looks them up, so under rule 2 they cannot live in this file —
 * and a second copy of a message, in a repository that never reads it, is a copy
 * that drifts.
 *
 * The API's catalogue is `src/content/dashboard-ar.ts` in
 * `Teslm-App/teslm-security-backend`, and it keeps all of them. If a message
 * needs rewording, that is where it changes.
 */

export const app = {
  name: "منصة العمليات",
  nameFull: "تسلم المتحدة للحراسات الأمنية — منصة العمليات",
  skipToContent: "تخطَّ إلى المحتوى",
} as const;

/** Sidebar groups, in order. Flat lists stop working at nine destinations. */
export const nav = {
  groups: {
    monitoring: "المتابعة",
    operations: "العمليات",
    people: "الأفراد",
    analytics: "التحليلات",
    system: "النظام",
  },
  overview: "نظرة عامة",
  tracking: "التتبع المباشر",
  shifts: "المناوبات",
  zones: "المناطق",
  leaveTypes: "أنواع الإجازات",
  leave: "طلبات الإجازة",
  breaks: "أذونات اللحظة",
  deviceChanges: "تغيير الجوال والجهاز",
  incidents: "البلاغات الأمنية",
  maintenance: "الصيانة",
  updates: "التحديثات",
  policies: "السياسات",
  attendance: "الحضور",
  employees: "الموظفون",
  payrollAdjustments: "الخصومات والمكافآت",
  payrollRuns: "مسيّرات الرواتب",
  waitingList: "قائمة الانتظار",
  companies: "الشركات",
  reports: "التقارير",
  settings: "الإعدادات",
  notifications: "الإشعارات",
  audit: "سجل التغييرات",
  collapse: "تصغير القائمة",
  expand: "توسيع القائمة",
  console: "لوحة العمليات",
} as const;

/**
 * Shared vocabulary.
 *
 * Deliberately tiny, and it grows one word at a time as a screen needs it.
 * An earlier draft of this block held twenty-two entries for buttons and states
 * that did not exist yet — which is precisely the habit
 * `scripts/check-content-keys.mjs` exists to break, and it duly failed on all of
 * them. The prototype's `common` block was the same shape and shipped that way.
 */
export const common = {
  close: "إغلاق",
  /* A boolean rendered as a word, where a checkbox would be wrong — a read-only
     cell in a table. */
  yes: "نعم",
  no: "لا",
  /* Announced once per loading page, to a screen reader only. Forty grey
     rectangles are `aria-hidden`; this is what is heard instead. */
  loading: "جارٍ تحميل البيانات…",
  /* "٠ من أصل ٢" rather than "٠ / ٢". A slash between two numerals in an
     RTL paragraph is bidi-neutral, so the run reorders and the ratio renders
     visually reversed — a reader sees 2/0 where the data says 0 of 2. A word
     cannot be reordered. Shared, because coverage is stated the same way on the
     zones list and on every rota cell. */
  ofRequired: "من أصل",
  /* Shown above forms that mix required and optional fields. The asterisk itself
     is aria-hidden on each label; this sentence is what a reader hears as the
     legend before tabbing through. */
  requiredFieldsNote: "الحقول المعلّمة بـ * مطلوبة.",
  fieldRequired: "هذا الحقل مطلوب.",
  saveFailedRequired: "تعذّر الحفظ — أكمل الحقول المطلوبة.",

  /* Colour names, not hex codes. A swatch whose accessible name is "#f78d2e"
     tells a screen-reader user nothing about what they are choosing. Shared,
     because zones and shift templates both offer a palette. */
  colors: {
    orange: "برتقالي",
    rust: "طوبي",
    wine: "نبيتي",
    plum: "بنفسجي",
    blue: "أزرق",
    navy: "كحلي",
    teal: "أزرق مخضر",
    green: "أخضر",
    olive: "زيتي",
  },
} as const;

/**
 * One status vocabulary for the whole console. The prototype had a different
 * pill per screen; the same state must read the same everywhere.
 *
 * @dynamic — always reached as `status[row.status]`, never by literal key, so
 * the orphan check cannot see the usages textually.
 */
export const status = {
  active: "نشط",
  inactive: "موقوف",
  pending: "قيد المراجعة",
  approved: "معتمد",
  rejected: "مرفوض",
  draft: "مسودة",
  published: "منشور",
  onDuty: "على رأس العمل",
  offDuty: "خارج الدوام",
  noSignal: "لا توجد إشارة",
  insideZone: "داخل المنطقة",
  outsideZone: "خارج المنطقة",
  late: "متأخر",
  onTime: "في الموعد",
  absent: "غائب",
} as const;

/**
 * @dynamic — reached as `roles[user.role]`.
 */
export const roles = {
  admin: "مدير النظام",
  supervisor: "مشرف",
  guard: "حارس أمن",
  /* A client company's own login. Reached the same way as the other three, and
     its absence was a compile error the moment `Role` grew a fourth member —
     which is the outcome `roles[user.role]` is meant to produce. */
  company: "شركة عميلة",
} as const;

export const auth = {
  signInTitle: "تسجيل الدخول",
  signInLead: "منصة العمليات مخصصة لمديري النظام والمشرفين.",
  identifier: "البريد الإلكتروني أو رقم الجوال",
  password: "كلمة المرور",
  signIn: "دخول",
  signingIn: "جارٍ الدخول…",
  signOut: "تسجيل الخروج",
  forgotPassword: "نسيت كلمة المرور؟",
  /* The reveal toggle names the action, not the state — and it flips, so the
     label is never stale. */
  showPassword: "إظهار كلمة المرور",
  hidePassword: "إخفاء كلمة المرور",

  /* The prototype had none of the following, in any form. A guard whose account
     was created could not learn their password, and nobody who forgot one could
     recover it. */
  forgotTitle: "استعادة كلمة المرور",
  forgotLead: "أدخل رقم الهوية / الإقامة المسجّل لدينا وسنرسل رمز تحقق إلى جوالك.",
  forgotSubmit: "إرسال الرمز",
  forgotSent: "إذا كان البريد مسجلًا لدينا فسيصل الرابط خلال دقائق.",
  nationalIdLabel: "رقم الهوية / الإقامة",
  nationalIdHint: "١٠ أرقام تبدأ بـ ١ أو ٢.",
  otpTitle: "أدخل رمز التحقق",
  otpLead: "أرسلنا رمزًا إلى الجوال",
  otpLabel: "رمز التحقق",
  otpSubmit: "تحقق من الرمز",
  otpResend: "إعادة إرسال الرمز",
  otpResendIn: "إعادة الإرسال بعد",
  otpVerifying: "جارٍ التحقق…",
  otpSeconds: "ث",
  passwordResetDone: "تم تعيين كلمة المرور. سجّل الدخول بالحساب.",
  sessionSupersededBanner: "تم تسجيل الدخول من جهاز آخر. انتهت هذه الجلسة.",

  resetTitle: "تعيين كلمة مرور جديدة",
  resetLead: "اختر كلمة مرور جديدة لحسابك.",
  resetSubmit: "تعيين كلمة المرور",
  newPassword: "كلمة المرور الجديدة",
  confirmPassword: "تأكيد كلمة المرور",
  passwordHint: "٨ أحرف على الأقل.",
  emailLabel: "البريد الإلكتروني",
  sending: "جارٍ الإرسال…",
  saving: "جارٍ الحفظ…",
  backToSignIn: "العودة إلى تسجيل الدخول",
  requestNewLink: "طلب رابط جديد",

  /* A spent or expired link is told apart from an invalid one. The token is
     unguessable, so saying which it is reveals nothing to anyone else — and
     "request a new link" is only actionable if you know that is the problem. */
  linkExpiredTitle: "انتهت صلاحية الرابط",
  linkInvalidTitle: "الرابط غير صحيح",
  linkExpiredLead: "الروابط صالحة لمدة محدودة. اطلب رابطًا جديدًا وسيصلك على بريدك.",
  linkUsedLead: "تم استخدام هذا الرابط مسبقًا. اطلب رابطًا جديدًا إذا كنت بحاجة إليه.",
  inviteExpiredLead: "انتهت صلاحية رابط التنشيط. راجع مدير النظام ليرسل لك رابطًا جديدًا.",
  inviteUsedLead: "تم تنشيط الحساب مسبقًا. سجّل الدخول بكلمة المرور التي اخترتها.",

  inviteTitle: "تنشيط الحساب",
  inviteLead: "مرحبًا بك. اختر كلمة مرور لتنشيط حسابك.",
  inviteSubmit: "تنشيط الحساب",

  resubmitTitle: "تعديل بيانات الطلب",
  resubmitLead: "عدّل بياناتك وأعد إرسال الطلب للمراجعة. يعود الحساب إلى حالة انتظار الموافقة.",
  resubmitReasonLabel: "سبب الرفض السابق",
  resubmitName: "الاسم",
  resubmitPhone: "رقم الجوال",
  resubmitSubmit: "إعادة الإرسال للموافقة",
  resubmitDoneTitle: "تم إعادة إرسال الطلب",
  resubmitDoneLead: "طلبك الآن بانتظار موافقة المدير. ستصلك رسالة عند اتخاذ القرار.",
  resubmitExpiredLead: "انتهت صلاحية رابط التعديل. تواصل مع مدير النظام لطلب رابط جديد.",
  resubmitUsedLead: "تم استخدام هذا الرابط مسبقًا. إذا احتجت تعديلًا آخر، تواصل مع مدير النظام.",
  resubmittedBanner: "تم إعادة إرسال طلبك وهو بانتظار الموافقة.",

  /* Errors are specific on purpose. The prototype collapsed every server error
     code — email_in_use, invalid_supervisor, missing_fields, upload_error —
     into one "حدث خطأ ما". */
  invalidCredentials: "البريد أو كلمة المرور غير صحيحة.",
  linkExpired: "انتهت صلاحية الرابط. اطلب رابطًا جديدًا.",
  passwordTooShort: "كلمة المرور يجب أن تكون ٨ أحرف على الأقل.",
  passwordMismatch: "كلمتا المرور غير متطابقتين.",
  nationalIdInvalid: "يجب أن يتكون رقم الهوية من ١٠ أرقام ويبدأ بـ ١ أو ٢.",
  nationalIdNotFound: "رقم الهوية هذا غير مسجّل في النظام.",
  phoneMissing: "لا يوجد رقم جوال مرتبط بالحساب. راجع مدير النظام.",
  otpInvalid: "رمز التحقق غير صحيح.",
  otpExpired: "انتهت صلاحية الرمز. اطلب رمزًا جديدًا.",
  otpLocked: "محاولات كثيرة على الرمز. اطلب رمزًا جديدًا.",
  smsUnavailable: "تعذّر إرسال الرسالة. أعد المحاولة لاحقًا.",
  otpResendWait: "انتظر قليلًا قبل إعادة إرسال الرمز.",
  rateLimited: "محاولات كثيرة. انتظر قليلًا ثم أعد المحاولة.",
} as const;

/** Where a guard lands. The prototype dead-ended them on a bare text node. */
export const mobileOnly = {
  title: "هذه المنصة للمشرفين ومديري النظام",
  lead: "سجّل حضورك وقدّم طلباتك من تطبيق الحراس على جوالك.",
} as const;

export const errors = {
  forbiddenTitle: "لا تملك صلاحية الوصول",
  /* A silent redirect reads as a broken link. The prototype bounced a
     supervisor away from /zones with no message at all. */
  forbiddenLead: "هذه الصفحة متاحة لدور مختلف. راجع مدير النظام إذا كنت تحتاجها.",
  serverTitle: "تعذّر إكمال العملية",
  serverLead: "حدث خطأ غير متوقع. أعد المحاولة، وإذا تكرر أبلغ الدعم الفني.",
  signedInAs: "أنت مسجّل الدخول بصفة",
  goHome: "الذهاب إلى نظرة عامة",
  /* Shown by the segment error boundary. Deliberately says what to do next
     rather than apologising. */
  reload: "إعادة المحاولة",
  /* Client-side transport fallbacks in `src/config` — used when the API
     never returns a localised sentence (network down, empty body, 500 code). */
  sessionExpired: "انتهت الجلسة. سجّل الدخول من جديد.",
  sessionSuperseded: "تم تسجيل الدخول من جهاز آخر. سجّل الدخول من جديد للمتابعة.",
  networkUnavailable: "تعذّر الاتصال بالخدمة. تحقق من الشبكة وأعد المحاولة.",
  operationFailed: "تعذّر إكمال العملية. أعد المحاولة.",
  loginFailed: "تعذّر تسجيل الدخول. أعد المحاولة.",
  linkInvalid: "الرابط غير صالح.",
  internalError:
    "حدث خطأ غير متوقع في الخادم. أعد المحاولة، وإن تكرّر فأبلغ الدعم الفني.",
  internalErrorRequest: "برقم الطلب",
  capacityFull: "السعة مكتملة. فعّل إجباريًا أو ارفع الحد أو أوقف حسابًا آخر.",
  candidatesLoadFailed: "تعذّر تحميل قائمة الأفراد. حاول مرة أخرى.",
} as const;

/**
 * The Overview — built last, because every tile has to link to a filtered list
 * that already exists. The prototype's `/` was a bare role redirect.
 */
export const overview = {
  greeting: "مرحبًا،",
  lead: "ملخص التشغيل الآن.",

  /* KPI tiles. Each one links somewhere. */
  onDutyNow: "على رأس العمل الآن",
  ofActive: "من الحراس النشطين",
  coverageToday: "تغطية مناوبات اليوم",
  shiftsCovered: "مناوبة مغطاة",
  lateToday: "تأخير اليوم",
  onTimeToday: "في الموعد اليوم",
  pendingLeave: "طلبات إجازة قيد المراجعة",
  neverPinged: "بلا إشارة إطلاقًا",
  understaffedZones: "مناطق أقل من المطلوب",
  noneTile: "لا شيء",

  /* Coverage gaps: the panel that matters most. */
  gapsTitle: "نقص التغطية في الـ٢٤ ساعة القادمة",
  gapsLead: "مناوبات منشورة تبدأ قريبًا وعدد الحراس المعيّنين عليها أقل من المطلوب.",
  gapsEmpty: "كل المناوبات القادمة مغطاة.",
  gapsShort: "ناقص",
  gapsGuards: "حارس",
  gapsFix: "معالجة في الجدول",

  /* Alerts: things that need a human. */
  alertsTitle: "يحتاج انتباهك",
  alertsEmpty: "لا شيء يحتاج انتباهك الآن.",
  alertLeave: "طلبات إجازة تنتظر قرارك",
  alertUnclosed: "تسجيلات حضور بلا انصراف",
  alertStale: "حراس توقفت إشارتهم",
  alertGeofence: "تسجيلات خارج النطاق اليوم",
  alertUnderstaffed: "مناطق تحت التغطية المطلوبة",
  alertInvites: "حسابات لم يُنشّطها أصحابها",
  alertCompanyContracts: "عقود شركات تقترب من الانتهاء",

  /* Activity feed. */
  activityTitle: "آخر التسجيلات",
  activityEmpty: "لا تسجيلات بعد اليوم.",
  activityCheckIn: "سجّل حضوره",
  activityCheckOut: "سجّل انصرافه",
  activityOutside: "خارج النطاق",

  /* Sparkline. */
  trendTitle: "الالتزام بالمواعيد — ١٤ يومًا",
  trendLead: "نسبة من سجّل حضوره في موعده لكل يوم. الأيام بلا مناوبات مجدولة تظهر فارغة، لا صفرًا.",
  trendUnit: "نسبة مئوية",
  trendEmpty: "لا مناوبات مجدولة في الفترة.",
  trendLast: "آخر يوم مكتمل",
  trendChange: "التغير خلال الفترة",
  trendAverage: "متوسط الفترة",
  trendUpOne: "ارتفاع نقطة واحدة",
  trendUp: "ارتفاع {n} نقطة",
  trendDownOne: "انخفاض نقطة واحدة",
  trendDown: "انخفاض {n} نقطة",
  trendFlat: "بلا تغيّر",
  trendReport: "عرض التقرير",
  activityAll: "سجل الحضور كاملًا",
  incidentOpen: "عرض البلاغات المفتوحة",
  incidentBannerOne: "بلاغ طارئ لم يُستلم بعد",
  incidentBannerMany: "{count} بلاغات طارئة لم تُستلم بعد",
  maintenanceUntil: "تطبيق الحراس متوقف للصيانة حتى {time}",
  maintenanceManage: "إدارة الصيانة",

  /* Freshness. A dashboard with no timestamp cannot be told apart from a stale one. */
  asOf: "محدّث حتى",

} as const;

/** The Employees module. */
export const employees = {
  backToList: "العودة إلى الموظفين",
  title: "الموظفون",
  lead: "الموظفون النشطون فقط — الطلبات والحسابات غير النشطة في قائمة الانتظار.",

  newTitle: "إضافة موظف",
  newLead: "سيصل الموظف رابط تنشيط على بريده لاختيار كلمة المرور.",
  create: "إضافة الموظف",
  openWaitingList: "قائمة الانتظار",
  editTitle: "تعديل البيانات",
  save: "حفظ التعديلات",
  cancel: "إلغاء",

  /* Field labels. Every one sits above its input, never inside it as a
     placeholder — a placeholder disappears exactly when the user needs it. */
  name: "الاسم الكامل",
  email: "البريد الإلكتروني",
  phone: "رقم الجوال",
  phoneHint: "اختياري. مثال: ٠٥٠١٢٣٤٥٦٧",
  role: "الدور",
  supervisor: "المشرف المسؤول",
  supervisorHint: "مطلوب للحراس.",
  zonesLabel: "المناطق",
  zonesHint: "يمكن تعيين الحارس على أكثر من منطقة.",
  noSupervisor: "بدون مشرف",
  choose: "اختر…",
  supervisorNotActivated: "لم يُنشّط حسابه",
  noZonesYet: "لا توجد مناطق بعد. أضف منطقة أولًا من شاشة المناطق.",
  saving: "جارٍ الحفظ…",

  /* The record's tabs.

     Tabs rather than five routes: they are five views of one person, and an
     operator flips between them. The active tab is a search param, so a
     particular view is linkable and the back button steps through them.

     Only the open tab fetches. Opening a record costs one query, not five. */
  tabOverview: "البيانات",
  tabShifts: "المناوبات",
  tabAttendance: "الحضور",
  tabLeave: "الإجازات",
  tabAudit: "سجل التغييرات",

  historyWhen: "التاريخ",
  /* Every capped list says it is capped. A truncated list that does not admit it
     is worse than a short one — the reader has no way to tell. */
  historyCapped: "آخر ٢٠ سجل. الإجمالي:",
  historyShiftsWindow: "آخر ٦٠ مناوبة خلال سنة.",
  historyViewAll: "عرض الكل",
  auditBatchRow: "عملية جماعية",

  shiftsEmptyTitle: "لا مناوبات مسجّلة",
  shiftsEmptyLead: "لم تُسنَد لهذا الموظف أي مناوبة في آخر سنة. تُسنَد المناوبات من شاشة المناوبات.",
  attendanceEmptyTitle: "لا سجل حضور",
  attendanceEmptyLead: "لم يسجّل هذا الموظف أي حضور بعد. يُسجَّل الحضور من تطبيق الجوال.",
  leaveEmptyTitle: "لا طلبات إجازة",
  leaveEmptyLead: "لم يقدّم هذا الموظف أي طلب إجازة. تُقدَّم الطلبات من تطبيق الجوال.",
  auditEmptyTitle: "لا تغييرات مسجّلة",
  auditEmptyLead: "لم يُعدّل أحد بيانات هذا الموظف بعد إنشاء الحساب.",


  /* Table */
  columnName: "الاسم",
  columnRole: "الدور",
  columnSupervisor: "المشرف",
  columnZones: "المناطق",
  columnJobTitle: "المسمى الوظيفي",
  columnStatus: "الحالة",
  columnCreated: "تاريخ الإضافة",
  /* Sequential job number — global, assigned on create, never reused. */
  columnEmployeeNo: "الرقم الوظيفي",
  columnNationality: "الجنسية",
  columnGovernmentSupported: "الدعم الحكومي",
  exportReport: "تقرير الموظفين",
  columnLastLogin: "آخر دخول",

  searchPlaceholder: "ابحث بالاسم أو البريد أو الجوال أو الرقم الوظيفي",
  employeeNo: "الرقم الوظيفي",
  employeeNoHint: "يُعيَّن تلقائيًا عند الإضافة ولا يمكن تعديله.",
  employeeNoPending: "يُعيَّن تلقائيًا عند الحفظ",
  filterRole: "الدور",
  filterStatus: "الحالة",
  filterZone: "المنطقة",
  filterSupervisor: "المشرف",

  /* The account lifecycle, one label per `accountStatus` value plus the invite
     state that rides on top of `active`. Five statuses rather than the two the
     prototype had: a hire now waits for a decision, and a decision can land on a
     queue instead of on an account. */
  pendingInvite: "لم يُنشّط الحساب",
  statusActive: "نشط",
  statusPending: "بانتظار الموافقة",
  statusWaitingList: "قائمة الانتظار",
  statusSuspended: "موقوف",
  statusRejected: "مرفوض",
  /** FIFO rank while queued — `{n}` is the 1-based position in the company pool. */
  queuePosition: "ترتيب قائمة الانتظار",
  queuePositionValue: "الترتيب {n}",
  waitingListTitle: "قائمة الانتظار",
  waitingListLead:
    "كل من لم يُنشَّط بعد — طلبات جديدة، طابور المقاعد، موقوفون، ومرفوضون. صفِّ حسب الحالة، أو أضف موظفًا جديدًا من هنا.",
  waitingListEmptyTitle: "لا يوجد أحد في المسار",
  waitingListEmptyLead:
    "أضف موظفًا لبدء التعيين، أو وافق على الطلبات عند وصولها. يظهر النشطون في صفحة الموظفين.",
  columnWaitingSince: "تاريخ الدخول للطابور",
  backToEmployees: "العودة للموظفين",
  actionsLabel: "إجراءات",
  showMore: "عرض عدد أكبر في الصفحة",
  neverSignedIn: "لم يسجّل الدخول",
  noZones: "بدون منطقة",
  noPhone: "—",

  /* Bulk actions.

     The prototype had no multi-select anywhere, so standing down a departing
     shift of ten guards was ten trips through a row menu.

     Every string names a count. A bulk confirmation that does not say how many
     rows it covers is asking the operator to trust a selection they made across
     three pages of scrolling. */
  selected: "محدَّد",
  selectAll: "تحديد الكل",
  clearSelection: "إلغاء التحديد",
  bulkDeactivate: "إيقاف المحدَّدين",
  bulkReactivate: "تنشيط المحدَّدين",
  bulkAssignZones: "تعيين مناطق",

  bulkDeactivateTitle: "إيقاف الحسابات المحدَّدة",
  bulkDeactivateBody:
    "لن يستطيع أصحاب هذه الحسابات تسجيل الدخول، وستُنتهى جلساتهم الحالية فورًا — على الويب وعلى تطبيق الجوال. تبقى بياناتهم وسجل حضورهم كما هي.",
  bulkReactivateTitle: "إعادة تنشيط الحسابات المحدَّدة",
  bulkReactivateBody: "سيستطيع أصحاب هذه الحسابات تسجيل الدخول مرة أخرى بكلمة المرور نفسها.",

  bulkAssignZonesTitle: "تعيين مناطق للمحدَّدين",
  /* Said in the dialog, not only in a code comment: the prototype's quick-assign
     replaced a guard's zones instead of adding to them, and a guard left on zero
     zones is allowed to check in anywhere by the geofence rule. */
  bulkAssignZonesBody:
    "المناطق تُضاف إلى مناطق كل حارس الحالية ولا تحلّ محلها. لإزالة منطقة من حارس، استخدم صفحة الحارس أو صفحة المنطقة.",
  bulkAssignZonesConfirm: "إضافة المناطق",
  bulkAssignZonesPick: "المناطق المطلوب إضافتها",

  bulkSendSms: "إرسال رسالة نصية",
  bulkSendEmail: "إرسال بريد",
  bulkSendSmsTitle: "إرسال رسالة نصية للمحدَّدين",
  bulkSendEmailTitle: "إرسال بريد للمحدَّدين",
  bulkSendSmsBody:
    "تُرسل الرسالة إلى أرقام الجوال المسجَّلة للحسابات المحدَّدة. الحسابات بلا جوال صالح تُتخطّى.",
  bulkSendEmailBody:
    "يُرسل البريد إلى عناوين البريد المسجَّلة للحسابات المحدَّدة. الحسابات بلا بريد تُتخطّى.",
  bulkSendSmsConfirm: "إرسال الرسائل",
  bulkSendEmailConfirm: "إرسال البريد",
  bulkSendNotification: "إرسال إشعار",
  bulkSendNotificationTitle: "إرسال إشعار للمحدَّدين",
  bulkSendNotificationBody:
    "يصل الإشعار إلى تطبيق الحراس والمشرفين المحدَّدين: داخل التطبيق إن كان مفتوحًا، أو كإشعار على الجوال إن كان مغلقًا. حسابات الإدارة والعملاء وحسابك تُتخطّى.",
  bulkSendNotificationConfirm: "إرسال الإشعار",
  bulkMessageSubject: "الموضوع",
  bulkMessageTitle: "العنوان",
  bulkMessageBody: "نص الرسالة",
  bulkMessageBodyHintSms: "حتى ٥٠٠ حرف. تُرسل كما هي إلى الجوال.",
  bulkMessageBodyHintEmail: "حتى ٥٠٠٠ حرف.",
  bulkMessageBodyHintNotification: "حتى ١٢٠ حرفًا للعنوان و١٠٠٠ حرف للنص.",
  bulkSkippedSmsWhy: "لا يوجد رقم جوال صالح، أو الحساب خارج نطاق صلاحيتك.",
  bulkSkippedEmailWhy: "لا يوجد بريد، أو الحساب خارج نطاق صلاحيتك.",
  bulkSkippedNotificationWhy: "الحساب ليس لحارس أو مشرف، أو هو حسابك.",
  bulkSelectMessageFirst: "اكتب نص الرسالة أولًا.",
  bulkSelectSubjectFirst: "اكتب الموضوع ونص الرسالة أولًا.",
  bulkSelectNotificationFirst: "اكتب العنوان ونص الإشعار أولًا.",
  bulkEmailAttach: "إرفاق ملف",
  bulkEmailAttachHint: "اختياري — PDF أو صورة أو Word أو Excel. حتى ١٠ ميجابايت للملف، ٥ ملفات كحد أقصى.",
  bulkEmailAttachRemove: "إزالة المرفق",
  bulkEmailAttachmentTooLarge: "حجم الملف أكبر من ١٠ ميجابايت.",
  bulkEmailAttachmentTotalTooLarge: "إجمالي المرفقات أكبر من الحد المسموح للإرسال.",
  bulkEmailAttachmentLimit: "يمكن إرفاق ٥ ملفات كحد أقصى.",
  bulkEmailAttachmentType: "نوع الملف غير مسموح. المسموح: PDF أو صورة أو Word أو Excel.",

  bulkCount: "العدد:",
  bulkSkipped: "سيتم تجاوز:",
  bulkSkippedWhy: "حسابك الخاص، أو حساب حالته هي المطلوبة أصلًا.",
  /* The reason the row's own switch is off, said rather than left to be guessed
     from a greyed-out control. */
  cannotDeactivateSelf: "لا يمكنك إيقاف حسابك الخاص.",
  bulkSkippedZonesWhy: "المناطق تُعيَّن للحراس النشطين فقط، وداخل فريقك.",
  bulkSelectZonesFirst: "اختر منطقة واحدة على الأقل.",


  /* Actions */
  view: "عرض",
  edit: "تعديل",
  deactivate: "إيقاف الحساب",
  reactivate: "إعادة التنشيط",
  resendInvite: "إعادة إرسال رابط التنشيط",

  /* Confirmations. The prototype used window.confirm for its one destructive
     action and nothing at all for the rest. */
  deactivateTitle: "إيقاف الحساب",
  deactivateBody:
    "لن يستطيع الموظف تسجيل الدخول، وستُنتهى جلساته الحالية. تبقى بياناته وسجل حضوره كما هي.",
  deactivateConfirm: "إيقاف الحساب",
  reactivateTitle: "إعادة تنشيط الحساب",
  reactivateBody: "سيستطيع الموظف تسجيل الدخول مرة أخرى بكلمة المرور نفسها.",
  reactivateConfirm: "إعادة التنشيط",

  /* Deciding a hire.

     The approve dialog names both possible outcomes because the operator does
     not choose between them — the seat cap does — and being told afterwards that
     somebody went on a queue reads as a failed action. */
  approve: "الموافقة",
  reject: "الرفض",
  activateWaiting: "تفعيل من قائمة الانتظار",
  enqueueWaiting: "إضافة لقائمة الانتظار",
  approveTitle: "الموافقة على الحساب",
  approveBody:
    "إذا وُجد مقعد شاغر سيصبح الحساب نشطًا ويُرسل رابط التنشيط. إذا اكتملت السعة فسيُدرج في قائمة الانتظار.",
  /* نفس نتيجة الموافقة العادية، لكن للصف الذي كان مرفوضًا. */
  approveFromRejectedBody:
    "سيُراجع هذا قرار الرفض. إذا وُجد مقعد شاغر سيصبح الحساب نشطًا ويُرسل رابط التنشيط. إذا اكتملت السعة فسيُدرج في قائمة الانتظار.",
  enqueueWaitingTitle: "إضافة لقائمة الانتظار",
  enqueueWaitingBodyPending:
    "سيُدرج الحساب في قائمة الانتظار دون تفعيله الآن — حتى إن وُجد مقعد شاغر. سيظهر في شاشة قائمة الانتظار إلى أن تفعّله أو يُفعَّل تلقائيًا عند توفر مقعد.",
  enqueueWaitingBodyActive:
    "سيخرج الحساب من السعة النشطة، وستُنهى جلساته الحالية، وسيُدرج في قائمة الانتظار. إن وُجد شخص آخر في الطابور فقد يُفعَّل مكانه.",
  enqueueWaitingConfirm: "إضافة لقائمة الانتظار",
  rejectTitle: "رفض الحساب",
  rejectBody: "لن يتمكن الحساب من تسجيل الدخول. يجب كتابة سبب الرفض واختيار طريقة الإشعار.",
  rejectReason: "سبب الرفض",
  rejectChannel: "طريقة الإشعار",
  rejectChannelSms: "رسالة نصية فقط",
  rejectChannelEmail: "بريد فقط",
  rejectChannelBoth: "رسالة نصية وبريد",
  activateWaitingTitle: "تفعيل من قائمة الانتظار",
  activateWaitingBody:
    "سيُفعَّل الحساب فورًا (حتى إن لم يكن الأول في الطابور). إن لم يتوفر مقعد، يجب تأكيد التفعيل الإجباري.",
  activateWaitingForce: "تفعيل إجباري رغم امتلاء السعة",
  /* Mirrors the API's own refusal word for word, because the dialog compares the
     two to decide whether to offer the force checkbox — the result carries a
     sentence, not a machine code. See `approval-dialogs.tsx`. */
  capacityFull: "السعة مكتملة. فعّل إجباريًا أو ارفع الحد أو أوقف حسابًا آخر.",

  /* Outcomes, stated specifically. The prototype collapsed every server error
     into one "حدث خطأ ما" and reported success by silently closing the modal. */
  updated: "تم حفظ التعديلات.",

  emptyTitle: "لا يوجد موظفون نشطون بعد",
  emptyLead: "يُضاف الموظفون الجدد من قائمة الانتظار. بعد التفعيل يظهرون هنا.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو إزالة بعض عوامل التصفية.",
  clearFilters: "إزالة التصفية",

  /* The client this person works on. Read on the form, the filter and the table.
     A supervisor holds a company and a guard inherits it from their supervisor,
     which is why the guard-side copy says "chosen automatically" rather than
     offering a field that would let the two disagree. */
  company: "شركة عميلة",
  companyHint: "الشركة التي يعمل عليها هذا المشرف.",
  companyFromSupervisor: "تُحدَّد تلقائيًا من الشركة التي يعمل عليها المشرف.",
  noCompany: "بدون شركة",
  noCompaniesYet: "لا توجد شركات بعد — أضف شركة أولًا.",
  addCompany: "إضافة شركة",
  filterCompany: "الشركة",
  filterDepartment: "القسم",
  filterCity: "مدينة العمل",

  resetPassword: "إرسال رابط تعيين كلمة المرور",
  resetPasswordTitle: "إرسال رابط تعيين كلمة المرور",
  resetPasswordBody:
    "سيصل الموظف رابط على بريده يحدّد به كلمة مرور جديدة. كلمة مروره الحالية تظل صالحة حتى يعيّن الجديدة، وأي رابط أُرسل قبل ذلك يتوقف. وعند تعيينها ستُنتهى جلساته على كل الأجهزة.",
  resetPasswordConfirm: "إرسال الرابط",

  errors: {
    nameTooShort: "الاسم يجب أن يكون حرفين على الأقل.",
    nameTooLong: "الاسم طويل جدًا.",
    emailInvalid: "البريد الإلكتروني غير صحيح.",
    phoneInvalid: "رقم الجوال غير صحيح. مثال: ٠٥٠١٢٣٤٥٦٧",
    roleInvalid: "اختر دورًا صحيحًا.",
    supervisorRequired: "اختر المشرف المسؤول عن الحارس.",
    supervisorNotAllowed: "المشرف يُحدَّد للحراس فقط.",
    companyRequired: "اختر الشركة التي يعمل عليها المشرف.",
    companyNotAllowed: "الشركة تُحدَّد للمشرفين فقط.",
    rejectionReasonRequired: "اكتب سبب الرفض.",
  },
} as const;

/**
 * The Zones module.
 *
 * A zone is the boundary of a guarded site: what the geofence tests a punch
 * against, what the rota staffs, and what a report groups by.
 */
/**
 * The employee's HR record — the eight sections behind a person.
 *
 * Every key here is reached literally, so this group is deliberately left under
 * the orphan check. The groups that genuinely are computed — `genders`,
 * `bloodTypes`, `addressKinds`, `studyModes`, `contractStatuses` — are separate
 * exports carrying their own exemption marker, for exactly this reason:
 * exempting a hundred-key group to cover five lookups switches the check off for
 * all of them, and that check is what catches a control somebody designed and
 * never built.
 *
 * (Written without the marker word itself — `check-content-keys.mjs` greps the
 * leading comment for it, so even a sentence denying it counts as claiming it.)
 *
 * Separate from `employees` because that group is about the *account* — the
 * list, the invite, the role — and this is about the person. They are edited on
 * different screens by different people and the two were already the longest
 * groups in this file.
 */
export const employeeRecord = {
  /* The rail. HR file first, then operational history. */
  groupProfile: "ملف الموظف",
  groupEmployment: "التوظيف",
  groupHistory: "السجل",

  sectionOverview: "نظرة عامة",
  sectionJob: "البيانات الوظيفية",
  sectionPersonal: "البيانات الشخصية",
  sectionContact: "بيانات التواصل",
  sectionEmergency: "الطوارئ",
  sectionAddress: "العنوان",
  sectionEducation: "التعليم",
  sectionAdditional: "بيانات إضافية",
  sectionDocuments: "المستندات",
  sectionContract: "العقد والعمل",
  sectionBank: "الحساب البنكي",
  sectionInsurance: "التأمين",
  sectionLeave: "الإجازات",
  sectionLeaveTypes: "أنواع الإجازات",
  sectionLeaveRequests: "طلبات الإجازة",
  sectionShifts: "الورديات",
  sectionAttendance: "الحضور",
  sectionAudit: "سجل التدقيق",

  /* Personal information */
  employeeNo: "الرقم الوظيفي",
  employeeNoHint: "يُعيَّن تلقائيًا عند الإضافة ولا يمكن تعديله.",
  nationalId: "رقم الهوية",
  nationalIdHint: "١٠ أرقام، تبدأ بـ ١ أو ٢.",
  idType: "نوع الهوية",
  idTypeNational: "هوية وطنية",
  idTypeIqama: "إقامة",
  idExpiresOn: "انتهاء الهوية / الإقامة",
  nameArTitle: "الاسم بالعربي",
  nameEnTitle: "الاسم بالإنجليزي",
  firstName: "الاسم الأول",
  secondName: "الاسم الثاني",
  thirdName: "الاسم الثالث",
  lastName: "الاسم الأخير",
  fullNameHint: "الاسم الذي يظهر في كل الشاشات يتكوّن من هذه الأجزاء.",
  nationality: "الجنسية",
  religion: "الديانة",
  employeeType: "نوع الموظف",
  title: "اللقب",
  gender: "الجنس",
  maritalStatus: "الحالة الاجتماعية",
  bloodType: "فصيلة الدم",
  birthDate: "تاريخ الميلاد",
  iban: "الآيبان",
  gosiNumber: "رقم التأمينات الاجتماعية",
  disbursementMethod: "طريقة الصرف",
  disbursementUnset: "تُستنتج من الآيبان",
  disbursementBank: "تحويل بنكي",
  disbursementCash: "نقدًا",
  disbursementCheque: "شيك",
  ibanHint: "حساب الراتب. SA ومعاها ٢٢ رقم.",
  bankName: "اسم البنك",
  accountHolderName: "اسم صاحب الحساب",
  accountNumber: "رقم الحساب",
  phoneAlt: "رقم الجوال الثاني",
  governmentSupported: "تحت الدعم الحكومي",
  notes: "ملاحظات",

  /* Job */
  jobCompany: "الشركة / الفرع",
  jobStatus: "حالة الموظف",
  jobSupervisor: "المدير المباشر",
  jobZones: "المواقع / المناطق",
  jobEditAccount: "تعديل بيانات الحساب",
  employmentStartDate: "تاريخ المباشرة (أول يوم عمل فعلي للموظف)",
  workCity: "مدينة / منطقة العمل",

  profileStatusDraft: "مسودة",
  profileStatusComplete: "مكتمل",
  profileSaveDraft: "حفظ كمسودة",
  profileMarkComplete: "تعيين كمكتمل",
  profileDraftHint: "ملف الموارد البشرية ما زال مسودة — يمكن إكمال الحقول المطلوبة على مراحل.",
  profileCompleteHint: "اكتملت الحقول المطلوبة. يمكنك إعادته مسودة إذا طرأ تغيير.",

  /* Additional */
  iqamaOccupation: "المهنة في الإقامة",
  sponsorship: "الكفالة",
  vehicleType: "نوع المركبة",
  vehiclePlate: "رقم اللوحة",
  previousExperience: "الخبرة السابقة",
  languageLevel: "مستوى اللغة",

  /* Contract extras */
  workType: "نوع العمل",
  workTypeFullTime: "دوام كامل",
  workTypePartTime: "دوام جزئي",
  workTypeShifts: "ورديات",
  payMethod: "طريقة احتساب الأجر",
  payMethodMonthly: "شهري",
  payMethodDaily: "يومي",
  payMethodHourly: "بالساعة",
  payMethodPerCall: "بالمهمة",
  housingAllowance: "بدل سكن",
  transportAllowance: "بدل مواصلات",
  totalSalary: "إجمالي الراتب",

  /* Contact */
  contactPrimaryTitle: "بيانات الاتصال الأساسية",
  contactPrimaryHint: "يُغيَّر الجوال الأساسي والبريد من بيانات الحساب — وهما المستخدمان لتسجيل الدخول.",
  contactPrimaryEdit: "تعديل بيانات الحساب",
  contactsTitle: "جهات الاتصال",
  contactName: "الاسم",
  contactRelation: "صلة القرابة",
  contactPhone: "رقم الجوال",
  contactEmail: "البريد الإلكتروني",
  contactIsPrimary: "جهة الاتصال الأساسية",

  /* Address */
  addressKind: "نوع العنوان",
  region: "المنطقة",
  city: "المدينة",
  district: "الحي",
  buildingNo: "رقم المبنى",
  postalCode: "الرمز البريدي",
  additionalNo: "الرقم الإضافي",
  unitNo: "رقم الوحدة",
  addressIsPrimary: "العنوان الأساسي",
  mapTitle: "الموقع على الخريطة",
  coordinates: "الإحداثيات",
  markerHint: "ابحث عن المكان من الأعلى، أو اضغط على الخريطة لتحديد الموقع، واسحب العلامة لضبطها.",
  markerClear: "إزالة العلامة",
  noMarker: "لم يُحدَّد موقع.",

  /* Education */
  degree: "الدرجة",
  qualification: "المؤهل",
  graduationDate: "سنة التخرج",
  college: "الكلية",
  studyMode: "نظام الدوام",

  /* Insurance */
  policyNo: "رقم البوليصة",
  insuranceType: "نوع التأمين",
  insuranceProvider: "مقدم التأمين",
  insuranceClass: "درجة التأمين",
  holderName: "اسم حامل الوثيقة",
  annualCost: "التكلفة السنوية",
  issueDate: "تاريخ الإصدار",
  expiryDate: "تاريخ الانتهاء",
  active: "نشط",

  /* Contract */
  contractNo: "رقم العقد",
  contractType: "نوع العقد",
  jobTitle: "المسمى الوظيفي",
  department: "الإدارة",
  startDate: "تاريخ البداية",
  endDate: "تاريخ النهاية",
  probationEndDate: "نهاية فترة التجربة",
  probationInputMode: "طريقة إدخال فترة التجربة",
  probationByDays: "بالأيام",
  probationByDate: "بالتاريخ",
  probationDays: "مدة التجربة (أيام)",
  probationDaysHint: "تُحسب من تاريخ بداية العقد.",
  probationNeedsStart: "حدّد تاريخ البداية أولًا.",
  probationEndsOn: "تنتهي في",
  probationDaysUnit: "يوم من تاريخ البداية",
  probationBeforeStart: "التاريخ قبل بداية العقد.",
  basicSalary: "الراتب الأساسي",
  allowances: "البدلات",
  noticePeriodDays: "مدة الإشعار (أيام)",
  contractStatus: "حالة العقد",
  renewContract: "تجديد العقد",
  renewContractTitle: "تجديد العقد",
  renewContractLead:
    "سيُنشأ عقد جديد بنفس المدة، يبدأ من تاريخ نهاية العقد الحالي. راجع البيانات وعدّل قبل التأكيد — سيُحوَّل العقد السابق إلى منتهٍ.",

  terminateContract: "فسخ العقد",
  terminateContractTitle: "فسخ العقد",
  terminateContractLead:
    "سيُحوَّل العقد إلى مفسوخ، وسيُوقَف حساب الموظف، ويُرسل بريد إشعار. متاح خلال آخر ٦٠ يومًا قبل نهاية العقد.",
  terminateContractConfirm: "تأكيد الفسخ",
  terminationReason: "سبب الفسخ",
  terminationReasonPlaceholder: "اختر…",
  terminationReasonRoleNoLongerNeeded: "لم يعد الدور مطلوبًا",
  terminationReasonPoorPerformance: "أداء غير مرضٍ",
  terminationReasonEmployeeResignation: "استقالة الموظف",
  terminationReasonRestructuring: "إعادة هيكلة",
  terminationReasonOther: "سبب آخر",
  terminationReasonOtherLabel: "حدد السبب الآخر",
  terminationDetails: "شرح / تفاصيل إضافية",
  terminationDetailsHint: "ملاحظات داخلية تُسجَّل مع عملية الفسخ.",
  terminationShareDetails: "إرسال الشرح في بريد الموظف",
  terminationAttachment: "إرفاق مستند (اختياري)",
  terminationAttachmentPick: "اختيار ملف",
  terminationAttachmentUploading: "جارٍ الرفع…",
  terminationAttachmentRemove: "إزالة المرفق",
  terminationAttachmentHint: "PDF أو Word أو Excel أو صورة أو نص — حتى ١٠ ميجا.",
  terminationAttachmentTooLarge: "المرفق أكبر من المسموح (حد أقصى ١٠ ميجا).",
  terminationAttachmentType: "نوع الملف هذا غير مسموح كمرفق.",

  /* Documents */
  documentKind: "نوع المستند",
  documentNumber: "رقم المستند",
  issuePlace: "جهة الإصدار",
  attachment: "المرفق",
  attachmentOpen: "فتح المرفق",
  attachmentNone: "لا يوجد مرفق",
  documentsRestricted: "المستندات متاحة لمدير النظام فقط.",

  /* The attachment control inside the document form. The file goes straight
     to التخزين الخاص عبر رابط موقّع قصير العمر؛ النموذج لا يحمل إلا المفتاح. */
  attachmentField: "ملف المستند",
  attachmentHint: "‏JPG أو PNG أو WebP أو PDF، بحد أقصى ٨ ميجابايت.",
  attachmentUploading: "جارٍ الرفع…",
  attachmentUploaded: "تم الرفع — يُحفظ مع المستند عند الحفظ.",
  attachmentReplace: "استبدال الملف",
  attachmentChoose: "اختيار ملف",
  attachmentClear: "إزالة المرفق",
  attachmentCurrent: "مرفق محفوظ على هذا المستند.",
  attachmentTooLarge: "حجم الملف أكبر من ٨ ميجابايت.",
  attachmentPutFailed: "تعذّر رفع الملف إلى التخزين. أعد المحاولة.",

  /* Leave types */
  leaveTypesTitle: "تكوين إجازة الموظف",
  leaveTypesHint: "الأنواع المختارة فقط هي التي يمكن للموظف تقديم طلب بها.",
  entitlementDays: "عدد الأيام",
  entitlementPlaceholder: "الافتراضي",
  entitlementHint: "اتركه فارغًا لاستخدام العدد الافتراضي للنوع.",
  payPaid: "مدفوعة",
  payPartial: "مدفوعة جزئيًا",
  payUnpaid: "غير مدفوعة",
  grantedCount: "نوع مختار",

  ledgerTitle: "سجل رصيد الإجازات",
  ledgerHint:
    "الاستحقاق الشهري والترحيل (مع رصيد الربع الأخير) والتعديلات لسنة الخدمة من ذكرى التعيين. أي نوع له حد سنوي. يُقرَّب الرصيد المتبقي لأسفل إلى عدد صحيح.",
  ledgerKind: "القيد",
  ledgerDays: "الأيام",
  ledgerPeriod: "الفترة",
  ledgerEmpty:
    "لا قيود بعد — يُقسَّط الاستحقاق شهريًا من حد النوع، ويحدث الترحيل في ذكرى التعيين.",
  ledgerErrorTitle: "تعذّر تحميل سجل رصيد الإجازات",
  ledgerErrorLead:
    "تكوين أنواع الإجازة أعلاه يعمل؛ الذي فشل هو سجل الرصيد (الاستحقاق والترحيل) فقط. جرّب تحديث الصفحة.",
  ledgerKindOpening: "افتتاح",
  ledgerKindAccrual: "استحقاق",
  ledgerKindCarry: "ترحيل",
  ledgerKindAdjustment: "تعديل",

  /* Shared table + form chrome */
  add: "إضافة",
  edit: "تعديل",
  remove: "حذف",
  save: "حفظ",
  cancel: "إلغاء",
  saving: "جارٍ الحفظ…",
  saved: "تم حفظ التعديلات.",
  updatedBy: "آخر تعديل بواسطة",
  actions: "الإجراءات",
  choose: "اختر…",
  none: "—",
  removeTitle: "حذف السجل",
  removeBody: "سيُزال نهائيًا من ملف الموظف. لا يمكن التراجع عن هذا الإجراء.",
  removeConfirm: "احذف",

  capped: "معروض جزء من السجلات. الإجمالي:",

  emptyTitle: "لا توجد بيانات",
  emptyLead: "لم يُسجَّل أي شيء في هذا القسم بعد.",

  tabData: "البيانات",
  tabWork: "العمل والعقود",
  tabQualifications: "المؤهلات والخبرات",
  tabAttendanceLog: "سجل الدوام",
  tabPerformance: "الأداء",
  tabPayroll: "الرواتب",
  tabLog: "السجل",
  tabLogShifts: "المناوبات",
  tabLogChanges: "التغييرات",

  photoLabel: "الصورة الشخصية",
  photoHint: "JPG أو PNG أو WebP، وبحد أقصى ٨ ميجابايت. تُقصّ الصورة مربعًا وتظهر في الملف والقوائم.",
  photoReplace: "استبدال",
  photoRemove: "إزالة",
  photoChoose: "اختر صورة",
  photoTypeInvalid: "يُقبل JPG أو PNG أو WebP فقط.",
  photoSaved: "تم تحديث الصورة الشخصية.",
  photoSaveFailed: "تعذّر حفظ الصورة الشخصية.",

  dataEmail: "البريد الإلكتروني",
  dataPhone: "رقم الجوال",
  dataZones: "عدد المناطق",
  dataNoZone: "بدون منطقة",
  dataJoined: "تاريخ الإضافة",
  dataLastLogin: "آخر دخول",
  dataStatus: "الحالة",
  dataNever: "لم يسجّل الدخول",

  commitmentTitle: "نسبة الالتزام",
  commitmentUp: "ارتفاع {n} نقاط عن الشهر السابق",
  commitmentDown: "انخفاض {n} نقاط عن الشهر السابق",
  commitmentFlat: "بلا تغيّر عن الشهر السابق",
  weightPunctuality: "الالتزام بالمواعيد",
  weightHours: "الساعات المحتسبة من المجدولة",
  weightClean: "أيام العمل بلا مخالفات",
  weightShare: "{n}٪",
  howCalculated: "كيف تُحسب",
  howCalculatedBody:
    "٤٠٪ الالتزام بمواعيد بداية المناوبات · ٤٠٪ الساعات المحتسبة من المجدولة · ٢٠٪ أيام العمل بلا مخالفات. تُحتسب المناوبات المستحقة فقط: أيام الحضور والغياب.",
  kpiCountedHours: "ساعات محتسبة",
  kpiOfScheduled: "من {n} مجدولة",
  kpiLate: "مرات التأخير",
  kpiNone: "لا يوجد",
  kpiPrevious: "{n} في الشهر السابق",
  kpiOvertime: "وقت إضافي معتمد",
  kpiOvertimePending: "{n} بانتظار الاعتماد",
  kpiAbsences: "غياب",
  kpiViolations: "مخالفات",
  kpiOutOfZone: "خارج النطاق",
  kpiBreak: "تجاوز استراحة: {n}",
  kpiBreakNone: "تجاوز استراحة: —",
  weeklyCommitment: "الالتزام أسبوعيًا — ١٢ أسبوعًا",
  weeklyCommitmentLead:
    "النسبة لكل أسبوع من السبت إلى الجمعة. الخط المتقطع متوسط فريق المشرف في الشهر المختار.",
  weeklyHours: "الساعات أسبوعيًا",
  hoursScheduled: "مجدولة",
  hoursCounted: "ساعات محتسبة",
  hoursOvertime: "وقت إضافي معتمد",
  hoursAxis: "المحور بالساعات",
  latenessByWeekday: "التأخير حسب يوم الأسبوع",
  latenessEmpty: "لا تأخير في هذا الشهر.",
  monthDays: "أيام الشهر",
  legendPresent: "حضور",
  legendIssue: "حضور مع تأخير أو مخالفة",
  legendAbsent: "غياب",
  legendLeave: "إجازة",
  legendRest: "راحة أو عطلة",
  legendOvertime: "وقت إضافي معتمد",
  violationsTitle: "المخالفات في الشهر",
  violationsEmpty: "لا مخالفات في هذا الشهر.",
  openDay: "فتح اليوم",
  violationLate: "تأخير {n}",
  violationZone: "خارج النطاق {n}",
  teamAverage: "متوسط الفريق",
  attendanceDayLead: "تسجيلات هذا اليوم.",

  payrollTitle: "كشوف الرواتب",
  payrollMonth: "الشهر",
  payrollGross: "الإجمالي",
  payrollDeductions: "الخصومات",
  payrollNet: "الصافي",
  payrollStatus: "الحالة",
  payrollEstimated: "تقديري",
  payrollStatusApproved: "معتمد",
  payrollPaid: "مدفوع",
  payrollOpenLog: "سجل الدوام",
  payrollOpenStatement: "فتح الكشف",
  payrollFootnote:
    "«تقديري» يعني أن مسيّر الشهر لم يُعتمد بعد، فالمبلغ يتغيّر مع كل تعديل على الدوام أو البنود. «معتمد» مبلغ مُقفل، و«مدفوع» سُجّل دفعه.",
  payrollItemsTitle: "الخصومات والمكافآت",
  payrollItemsEmpty: "لا بنود لهذا الموظف.",
  payrollViewAll: "عرض الكل",
  payrollAdd: "إضافة خصم أو مكافأة",
  payrollKind: "النوع",
  payrollKindDeduction: "خصم",
  payrollKindBonus: "مكافأة",
  payrollAmount: "المبلغ",
  payrollReason: "السبب",
  payrollAddSubmit: "إضافة",
  payrollAdded: "تمت إضافة البند.",
  payrollPaidMonth: "هذا الشهر مُعتمد ولا يقبل بنودًا جديدة.",
  statementBack: "العودة إلى الكشوف",
  statementEarnings: "الاستحقاقات",
  statementDeductionItems: "بنود الخصم",
  statementBonuses: "المكافآت",
  earningBasic: "الراتب الأساسي",
  earningHousing: "بدل السكن",
  earningTransport: "بدل النقل",
  earningAllowances: "بدلات أخرى",
  earningOvertime: "وقت إضافي",
  statementPaidOn: "تاريخ الصرف",
  statementEmptyLine: "لا بنود.",
  statementNet: "الصافي",
  payrollApprove: "اعتماد",
  payrollApproved: "تم اعتماد الكشف.",
  payrollApproveFailed: "تعذّر اعتماد الكشف.",
  dataDevice: "الجهاز المرتبط",
  qualificationsCertificates: "المؤهلات والشهادات",
  qualificationsCertificatesLead: "الشهادات الدراسية والمهنية.",
  certSpecialty: "التخصص",
  certIssuer: "الجهة المانحة",
  certDate: "تاريخ التخرج",
  experienceTitle: "الخبرات السابقة",
  experienceLead: "جهات العمل السابقة ومددها.",
  experienceEmployer: "جهة العمل",
  experienceFrom: "من تاريخ",
  experienceTo: "إلى تاريخ",
  languagesTitle: "اللغات",
  languagesLead: "اللغات التي يتحدثها الموظف ومستواها.",
  languageName: "اللغة",
  languageNotes: "ملاحظات",
  languageBeginner: "مبتدئ",
  languageIntermediate: "متوسط",
  languageAdvanced: "متقدم",
  languageNative: "اللغة الأم",
  dutyScheduled: "مجدولة",
  dutyCounted: "محتسبة",
  dutyLateAfter: "تأخير بعد السماح",
  dutyApprovedOt: "إضافي معتمد",
  dutyPendingOt: "إضافي معلّق",
  dutyAbsences: "غياب",
  dutyCommitment: "الالتزام",
  dutyViolations: "مخالفات",
  dutyDay: "اليوم",
  dutyShift: "المناوبة",
  dutyIn: "الحضور",
  dutyOut: "الانصراف",
  dutyLate: "تأخير",
  dutyExtra: "إضافي",
  dutyNotes: "ملاحظات",
  dutyRest: "راحة أسبوعية",
  dutyLeaveNote: "إجازة",
  dutyAbsentNote: "غياب",
  dutyPendingNote: "{n} معلّق",
  adjustmentsTitle: "الخصومات والمكافآت",
  adjustmentsLead: "بنود الخصم والمكافأة لكل الموظفين.",
  adjustmentsEmpty: "لا بنود.",
  adjustmentsEmployee: "الموظف",
  logAttendance: "الحضور",
} as const;

/** @dynamic — reached as `genders[profile.gender]`. */
export const genders = {
  male: "ذكر",
  female: "أنثى",
} as const;

/** @dynamic — reached as `maritalStatuses[profile.maritalStatus]`. */
export const maritalStatuses = {
  single: "أعزب",
  married: "متزوج",
  divorced: "مطلّق",
  widowed: "أرمل",
} as const;

/**
 * @dynamic — reached as `bloodTypes[profile.bloodType]`.
 *
 * The eight groups, written the way a lab report writes them. Not translated:
 * "A+" is the international notation and an Arabic rendering of it would be a
 * label nobody would recognise on a card.
 */
export const bloodTypes = {
  "A+": "A+",
  "A-": "A-",
  "B+": "B+",
  "B-": "B-",
  "AB+": "AB+",
  "AB-": "AB-",
  "O+": "O+",
  "O-": "O-",
} as const;

/** @dynamic — reached as `studyModes[row.studyMode]`. */
export const studyModes = {
  full_time: "انتظام",
  part_time: "انتساب",
} as const;

/** @dynamic — reached as `addressKinds[row.kind]`. */
export const addressKinds = {
  national: "العنوان الوطني",
  work: "عنوان العمل",
  other: "عنوان آخر",
} as const;

/** @dynamic — reached as `contractStatuses[row.status]`. */
export const contractStatuses = {
  draft: "مسودة",
  active: "ساري",
  expired: "منتهي",
  terminated: "مفسوخ",
} as const;

export const zones = {
  backToZones: "العودة إلى المناطق",
  title: "المناطق",
  lead: "حدود المواقع المحروسة، وسياسة النطاق الجغرافي وعدد الحراس المطلوب لكل منها.",

  newTitle: "إضافة منطقة",
  newLead: "ارسم الحدود على المخطط أو الصق الإحداثيات. كلا الطريقتين تكفي وحدها.",
  create: "إضافة المنطقة",
  editTitle: "تعديل المنطقة",
  save: "حفظ التعديلات",
  cancel: "إلغاء",
  saving: "جارٍ الحفظ…",

  /* Fields */
  name: "اسم المنطقة",
  nameHint: "يظهر في الجداول والتقارير وعلى تطبيق الحراس.",
  color: "اللون",
  colorHint: "لتمييز المنطقة على الخريطة وفي الجداول.",
  policy: "سياسة النطاق الجغرافي",
  policyBlock: "منع التسجيل خارج الحدود",
  policyFlag: "قبول التسجيل مع تنبيه",
  policyBlockHint: "لن يستطيع الحارس تسجيل حضوره إلا داخل الحدود.",
  policyFlagHint:
    "يُقبل التسجيل ويُسجَّل أنه خارج الحدود، ويصل تنبيه للمشرف. مناسب لموقع حدوده تقريبية.",
  company: "الشركة العميلة",
  noCompany: "بدون شركة",
  requiredGuards: "الحراس المطلوبون لكل مناوبة",
  requiredGuardsHint: "يُقاس عليه نقص التغطية في جدول المناوبات.",
  notes: "ملاحظات",
  notesHint: "اختياري. رقم المسؤول في الموقع، أو أي شيء يحتاجه المشرف.",
  gate: "البوابة",
  gateHint: "اختياري. اسم البوابة التي يقف عليها الحارس في هذا الموقع.",

  /* Geometry */
  boundary: "حدود المنطقة",
  boundaryHint:
    "انقر على المخطط لإضافة نقطة، واسحب نقطة لتحريكها. أو الصق الإحداثيات في الحقل المجاور.",
  coordinates: "الإحداثيات",
  coordinatesHint: "سطر لكل نقطة: خط العرض ثم خط الطول. يمكن لصق GeoJSON كذلك.",
  coordinatesEmpty: "لا توجد نقاط بعد. انقر على المخطط أو الصق إحداثيات.",
  /* The address pin. Focusable, because arrow keys are the only way to place a
     point without a pointer. */
  markerLabel: "علامة الموقع — استخدم الأسهم لتحريكها",
  planTitle: "مخطط الحدود",
  planNote:
    "مخطط هندسي بمقياس رسم، بلا خريطة أساس. عند ربط مزوّد الخرائط ستظهر الحدود فوق صور الموقع نفسه.",
  /* The satellite provider, when it is on. */
  mapLoading: "جارٍ تحميل الخريطة…",
  mapUnavailable: "تعذّر تحميل الخريطة. تأكد من مفتاح الخرائط ومن السماح لـ maps.googleapis.com في الـ CSP.",
  mapKeyMissing: "الخريطة معروضة كرسم هندسي بدون صور جوية — مفتاح الخرائط غير مضبوط في هذه البيئة.",
  providerSubstituted:
    "مزوّد الخرائط المطلوب غير مفعّل في هذه النسخة، ويُعرض المخطط الهندسي بدلاً منه.",
  mapSearchLabel: "ابحث عن موقع على الخريطة",
  mapSearchPlaceholder: "ابحث عن مدينة أو حي أو معلم…",
  undoPoint: "حذف آخر نقطة",
  clearBoundary: "مسح الحدود",
  vertexCount: "عدد النقاط",
  area: "المساحة التقديرية",
  centre: "مركز المنطقة",
  detectedGeojson: "تم قراءة الإحداثيات كـ GeoJSON وتبديل ترتيب المحورين تلقائيًا.",
  detectedLines: "تم قراءة الإحداثيات سطرًا سطرًا.",

  /* Table */
  columnName: "المنطقة",
  columnPolicy: "سياسة النطاق",
  columnGuards: "الحراس",
  columnRequired: "المطلوب",
  columnArea: "المساحة",
  columnCreated: "تاريخ الإضافة",
  searchPlaceholder: "ابحث باسم المنطقة",
  filterPolicy: "سياسة النطاق",
  filterState: "الحالة",
  stateLive: "المناطق النشطة",
  stateArchived: "المؤرشفة",
  archivedBadge: "مؤرشفة",
  actionsLabel: "إجراءات",
  understaffed: "أقل من المطلوب",

  /* Members */
  membersTitle: "الحراس المعيّنون",
  membersLead: "التعيين إضافي: إضافة حارس هنا لا تلغي تعييناته في مناطق أخرى.",
  membersEmpty: "لا حراس معيّنون على هذه المنطقة.",
  assign: "تعيين حراس",
  assignHint: "اختر من الحراس النشطين غير المعيّنين على هذه المنطقة.",
  assignSubmit: "تعيين المحدَّدين",
  noAssignable: "لا يوجد حراس متاحون للتعيين. كل الحراس النشطين معيّنون هنا بالفعل.",
  remove: "إلغاء التعيين",
  removeTitle: "إلغاء تعيين الحارس",
  removeBody: "سيُلغى تعيين الحارس على هذه المنطقة فقط. تبقى تعييناته الأخرى وسجل حضوره كما هي.",
  removeBodyLastZone:
    "هذه آخر منطقة معيّن عليها هذا الحارس. بعد الإلغاء سيصبح مسموحًا له بتسجيل الحضور من أي مكان، لأن قاعدة النطاق الجغرافي تعتبر الحارس بلا مناطق غير مقيَّد.",
  removeConfirm: "إلغاء التعيين",
  memberSince: "معيّن من",
  onlyZone: "منطقته الوحيدة",
  memberInactive: "حساب موقوف",

  /* Actions */
  view: "عرض",
  edit: "تعديل",
  archive: "أرشفة المنطقة",
  restore: "إعادة التنشيط",
  archiveTitle: "أرشفة المنطقة",
  /* The consequence, stated. The prototype deleted zones behind a bare
     window.confirm and said nothing about the cascade. */
  archiveBody:
    "لن تظهر المنطقة في الجداول ولا في تعيينات جديدة. تبقى تعيينات الحراس وسجل الحضور محفوظة، ويمكن إعادة تنشيطها.",
  archiveImpactNone: "لا حراس معيّنون عليها حاليًا.",
  archiveImpactMembers: "عدد الحراس المعيّنين:",
  archiveImpactUnfenced:
    "منهم سيصبحون بلا منطقة نشطة، وسيُسمح لهم بتسجيل الحضور من أي مكان:",
  archiveConfirm: "أرشفة المنطقة",
  restoreTitle: "إعادة تنشيط المنطقة",
  restoreBody: "ستعود المنطقة إلى الجداول وإلى تعيينات المناوبات بتعييناتها السابقة.",
  restoreConfirm: "إعادة التنشيط",

  /* Outcomes */
  updated: "تم حفظ التعديلات.",
  archived: "تم أرشفة المنطقة.",
  assigned: "تم تعيين الحراس.",
  unassigned: "تم إلغاء التعيين.",

  emptyTitle: "لا توجد مناطق بعد",
  emptyLead: "أضف أول منطقة، وسيصبح بإمكانك تعيين الحراس عليها وبناء جدول المناوبات.",
  emptyArchivedTitle: "لا توجد مناطق مؤرشفة",
  emptyArchivedLead: "المناطق المؤرشفة تظهر هنا، ويمكن إعادة تنشيطها.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو إزالة عوامل التصفية.",
  clearFilters: "إزالة التصفية",

  /* A supervisor is not read-only here any more: they may post their own
     guards. What they cannot do is define a zone. The note says which is which,
     rather than leaving a missing button to be interpreted. */
  readOnlyNote:
    "يمكنك تعيين حراس فريقك على المناطق. تعريف المناطق وحدودها وسياسة النطاق الجغرافي من صلاحيات مدير النظام.",

  errors: {
    nameTooShort: "اسم المنطقة يجب أن يكون حرفين على الأقل.",
    nameTooLong: "اسم المنطقة طويل جدًا.",
    gateTooLong: "اسم البوابة طويل جدًا.",
    colorInvalid: "اللون يجب أن يكون بصيغة #RRGGBB.",
    requiredGuardsInvalid: "عدد الحراس يجب أن يكون بين ١ و ٩٩.",
    notesTooLong: "الملاحظات طويلة جدًا.",

    /* One message per geometric problem `validatePolygon` can report. The
       prototype accepted any array of three or more points, so a self-crossing
       or lat/lng-swapped ring saved cleanly and the geofence then behaved
       unpredictably against it — with no message to explain anything. */
    boundaryEmpty: "ارسم الحدود أو الصق الإحداثيات.",
    boundaryUnparseable: "تعذّر قراءة الإحداثيات. استخدم سطرًا لكل نقطة: خط العرض، خط الطول.",
    boundaryOddCount: "عدد الأرقام غير مكتمل — آخر نقطة ناقصة.",
    tooFewPoints: "الحدود تحتاج ثلاث نقاط على الأقل.",
    outOfRange: "قيمة إحداثي خارج المدى المسموح.",
    looksSwapped:
      "يبدو أن خط العرض وخط الطول متبادلان: الحدود بهذا الترتيب تقع خارج نطاق عمل الشركة تمامًا.",
    zeroArea: "الحدود بلا مساحة — النقاط على استقامة واحدة أو متطابقة.",
    selfIntersecting: "حدود المنطقة تتقاطع مع نفسها.",
    spansAntimeridian: "الحدود تمتد حول الكرة الأرضية. راجع الإحداثيات.",
    tooLarge: "الحدود أكبر من أن تكون موقعًا محروسًا. راجع الإحداثيات.",

    noGuardsSelected: "اختر حارسًا واحدًا على الأقل.",
  },
} as const;

/**
 * The Shifts module — the rota.
 *
 * The prototype had no schedule: one static `HH:mm` pair per guard, forever. So
 * this vocabulary is entirely new, and it is the vocabulary Reports later
 * measures against.
 */
export const shifts = {
  title: "المناوبات",
  lead: "جدول المناوبات الأسبوعي لكل منطقة، مع التغطية المطلوبة والفعلية.",

  /* The rota grid */
  weekOf: "أسبوع",
  previousWeek: "الأسبوع السابق",
  nextWeek: "الأسبوع التالي",
  thisWeek: "هذا الأسبوع",
  filterZone: "المنطقة",
  gridZoneColumn: "المنطقة",
  addShift: "إضافة مناوبة",
  addShiftTo: "إضافة مناوبة في",
  emptyDay: "لا مناوبات",
  noAssignments: "لا حراس معيّنون على هذه المناوبة بعد.",
  shiftNotes: "ملاحطات المناوبة",

  /* The week's state, in one line above the grid. */
  summaryShifts: "المناوبات",
  summaryAssigned: "المعيَّنون",
  summaryRequired: "المطلوب",
  summaryGaps: "نقص التغطية",
  summaryDrafts: "مسودات",
  allPublished: "الأسبوع منشور بالكامل.",
  noShiftsThisWeek: "لا مناوبات في هذا الأسبوع بعد.",
  noZones: "لا توجد مناطق. أضف منطقة أولًا، فالمناوبة تُبنى على منطقة.",
  noZonesScoped: "لا حراس من فريقك معيّنون على أي منطقة بعد.",

  /* Publishing */
  publish: "نشر الأسبوع",
  publishTitle: "نشر جدول الأسبوع",
  publishBody:
    "سيصبح الجدول مرئيًا للحراس على التطبيق، ويُحتسب عليه الالتزام بالمواعيد والتغطية. يمكن تعديل التعيينات بعد النشر.",
  publishCount: "عدد المسودات التي ستُنشر:",
  publishGapWarning: "مناوبات ستُنشر وهي أقل من التغطية المطلوبة:",
  publishConfirm: "نشر",
  published: "تم نشر الجدول.",
  draftBadge: "مسودة",
  publishedBadge: "منشورة",

  /* Copying a week forward */
  copyPrevious: "نسخ الأسبوع السابق",
  copyTitle: "نسخ الأسبوع السابق",
  copyBody:
    "ستُنسخ مناوبات الأسبوع السابق وتعييناتها إلى هذا الأسبوع كمسودات. المناوبات الموجودة لن تُكرَّر.",
  copyConfirm: "نسخ",
  copied: "تم نسخ مناوبات الأسبوع السابق.",
  /* A shift cell */
  coverage: "التغطية",
  understaffed: "أقل من المطلوب",
  overnight: "تعبر منتصف الليل",
  removeShift: "حذف المناوبة",
  removeShiftTitle: "حذف المناوبة",
  removeShiftBody: "ستُحذف المناوبة وتعييناتها. لا يؤثر ذلك على سجل الحضور المسجَّل مسبقًا.",
  removeShiftBodyPublished:
    "هذه المناوبة منشورة، والحراس المعيّنون عليها يرونها على التطبيق. سيُلغى ظهورها لهم.",
  removeShiftConfirm: "حذف المناوبة",
  shiftRemoved: "تم حذف المناوبة.",
  shiftCreated: "تمت إضافة المناوبة.",

  /* Assignment */
  assignGuard: "تعيين حارس",
  assignHint: "الحراس المعيّنون على هذه المنطقة فقط — المنطقة هي موقع المناوبة.",
  assignSubmit: "تعيين",
  assigned: "تم التعيين.",
  unassign: "إلغاء التعيين",
  noCandidates: "لا حراس معيّنون على هذه المنطقة. عيّنهم من شاشة المناطق أولًا.",
  conflictShiftLabel: "مناوبة أخرى في نفس الوقت:",
  conflictLeaveLabel: "إجازة معتمدة في هذا الوقت",
  markAbsent: "تسجيل غياب",
  absentBadge: "غائب",
  coveredBadge: "تمت التغطية",

  /* Templates */
  templatesTitle: "قوالب المناوبات",
  templatesLead: "المناوبة تُبنى على قالب: نهارية، ليلية، أو أي نمط تحدده. القالب يحدد الوقت والاستراحة.",
  templatesLink: "قوالب المناوبات",
  backToRota: "العودة إلى الجدول",
  newTemplate: "إضافة قالب",
  editTemplate: "تعديل القالب",
  templateName: "اسم القالب",
  templateNameHint: "مثال: نهارية ٨–١٦، ليلية ٢٠–٤.",
  startTime: "وقت البداية",
  endTime: "وقت النهاية",
  timeHint: "بتوقيت الرياض، بصيغة ٢٤ ساعة.",
  crossesMidnight: "تعبر منتصف الليل",
  crossesMidnightHint:
    "حدّدها للمناوبة الليلية، مثل ٢٠:٠٠ إلى ٠٤:٠٠. تُحسب المدة على أساسها، ولا تُستنتج من الأرقام.",
  breakMinutes: "الاستراحة (دقائق)",
  breakHint: "تُخصم من مدة المناوبة المحتسبة.",
  templateColor: "اللون",
  templateColorHint: "لتمييز القالب في الجدول.",
  duration: "المدة المحتسبة",
  span: "الفترة",
  columnUsage: "الاستخدام",
  usageUpcoming: "قادمة",
  usageNone: "غير مستخدم",
  saveTemplate: "حفظ القالب",
  createTemplate: "إضافة القالب",
  templateUpdated: "تم حفظ القالب.",
  archiveTemplate: "أرشفة القالب",
  archiveTemplateTitle: "أرشفة القالب",
  archiveTemplateBody:
    "لن يظهر القالب عند إضافة مناوبة جديدة. المناوبات المبنية عليه تبقى كما هي بأوقاتها.",
  archiveTemplateUpcoming: "مناوبات قادمة مبنية على هذا القالب:",
  archiveTemplateConfirm: "أرشفة",
  templateArchived: "تم أرشفة القالب.",
  restoreTemplate: "إعادة التنشيط",
  restoreTemplateTitle: "إعادة تنشيط القالب",
  restoreTemplateBody: "سيعود القالب إلى قائمة الاختيار عند إضافة مناوبة.",
  restoreTemplateConfirm: "إعادة التنشيط",
  templatesEmptyTitle: "لا قوالب بعد",
  templatesEmptyLead: "أضف قالبًا واحدًا على الأقل — المناوبة تُبنى عليه.",
  showArchivedTemplates: "إظهار المؤرشفة",
  hideArchivedTemplates: "إخفاء المؤرشفة",
  archivedBadge: "مؤرشف",

  readOnlyNote: "لديك صلاحية العرض فقط على الجدول.",

  errors: {
    nameTooShort: "اسم القالب يجب أن يكون حرفين على الأقل.",
    nameTooLong: "اسم القالب طويل جدًا.",
    timeInvalid: "الوقت يجب أن يكون بصيغة ٢٤ ساعة، مثل ٠٨:٠٠.",
    /* The zero-length shift the prototype could not distinguish from a 24-hour
       one, because it inferred midnight-crossing instead of storing it. */
    zeroLength:
      "بداية المناوبة ونهايتها متطابقتان. حدّد «تعبر منتصف الليل» إذا كانت مناوبة ٢٤ ساعة.",
    endBeforeStart:
      "وقت النهاية قبل وقت البداية. حدّد «تعبر منتصف الليل» إذا كانت المناوبة ليلية.",
    breakInvalid: "الاستراحة يجب أن تكون بين ٠ و ٤٨٠ دقيقة.",
    breakTooLong: "الاستراحة أطول من مدة المناوبة.",
    colorInvalid: "اللون يجب أن يكون بصيغة #RRGGBB.",
    requiredGuardsInvalid: "عدد الحراس يجب أن يكون بين ١ و ٩٩.",
    dateInvalid: "التاريخ غير صحيح.",
    /* Double-booking: the classic rota defect, invisible until the day. */
    notesTooLong: "الملاحظات طويلة جدًا.",
    generic: "تعذّر إكمال العملية. أعد المحاولة.",
  },
} as const;

/**
 * Leave requests.
 *
 * @dynamic — `leaveGroups[row.leaveType.group]` is reached by computed key, so
 * the orphan check cannot see those usages textually.
 *
 * The *type names* are deliberately not here. There were five, hardcoded as
 * `leaveSubtypes`, and there are now fourteen that HR changes by policy — so
 * they live in `leave_types` and arrive on every row that needs one. A
 * catalogue entry per type would be a second place to add one, and the one
 * nobody remembers.
 */
export const leaveGroups = {
  annual: "سنوية",
  sick: "مرضية",
  family: "عائلية",
  religious: "دينية",
  study: "دراسية",
  other: "أخرى",
} as const;

export const leave = {
  title: "طلبات الإجازة",

  /* Filing on somebody's behalf — the console's first write *into* the queue
     rather than onto it, and the only way an admin's or a supervisor's own
     leave gets recorded at all. */
  fileTitle: "تسجيل إجازة",
  fileIntro: "سجّل إجازة نيابة عن الموظف. القرار يظل على المشرف كالمعتاد.",
  fileEmployee: "الموظف",
  fileSearchPlaceholder: "اختر أو ابحث بالاسم أو الرقم الوظيفي…",
  fileNoMatches: "لا نتائج مطابقة",
  fileSearchMore: "النتائج أكثر من المعروض. واصل الكتابة لتضييق البحث.",
  fileRemainingTitle: "الإجازات المتبقية",
  fileOf: "من",
  fileSubstitute: "البديل أثناء الغياب",
  fileSubstituteHint: "اختياري — موظف آخر يغطي فترة الإجازة.",
  fileOptional: "اختياري",
  fileType: "نوع الإجازة",
  fileStart: "من تاريخ",
  fileEnd: "إلى تاريخ",
  /* Inclusive calendar days — same rule as `countLeaveDays` on the API
     (من ١٠ إلى ١٥ سبتمبر = ٦ أيام). Annual types may still skip official
     holidays when the balance is charged; this figure is the span the
     operator picked, so they can check it before submit. */
  fileDuration: "عدد الأيام",
  fileNotes: "ملاحظات",
  fileSubmit: "تسجيل الطلب",
  fileOpen: "تسجيل إجازة",
  fileNoTypes: "هذا الموظف ليست لديه أنواع إجازات متاحة. حدّدها من ملفه في تبويب أنواع الإجازات.",
  fileAvailableBalance: "المتاح من هذا النوع",
  fileAvailableBalanceUnit: "يوم",
  fileAvailableBalanceLoading: "جارٍ تحميل الرصيد…",
  fileUnlimitedType: "هذا النوع غير محدود بعدد أيام.",
  fileOverBalance: "المدة المختارة أطول من المتاح، ولا تُقبل إلا كسلفة إجازة.",
  fileAdvance: "سلفة إجازة بموافقة",
  fileAdvanceHint: "يسمح بتجاوز الرصيد المتاح. يُسجَّل باسمك في السجل.",
  fileAvailableBalanceMissing: "لا يوجد رصيد محسوب لهذا النوع على الموظف.",
  fileAnnualEntitlement: "الاستحقاق السنوي",
  fileMissingEmploymentStart: "تاريخ المباشرة غير مسجّل، ولا يمكن حساب الرصيد.",
  filePendingNote:
    "سيُسجَّل الطلب باسمك ويظل قيد المراجعة — يجب أن يعتمده أحد مثل أي طلب آخر.",
  lead: "طلبات الحراس، والقرار فيها مع سببه.",

  /* Queue */
  tabPending: "قيد المراجعة",
  tabApproved: "معتمدة",
  tabRejected: "مرفوضة",
  tabAll: "الكل",
  searchPlaceholder: "ابحث باسم الحارس أو في الملاحظات",
  filterGroup: "التصنيف",
  filterLeaveType: "النوع",
  filterGuard: "الحارس",
  filterFrom: "من تاريخ",
  filterTo: "إلى تاريخ",
  dateRangeHint: "يُدرج كل طلب يتقاطع مع الفترة، لا الطلبات المحصورة فيها فقط.",
  /* A native date input renders in the *browser's* locale, so it can show
     "mm/dd/yyyy" inside an Arabic console. The applied range is echoed back in
     the console's own format so the operator can check what is actually
     filtered, whatever the input looks like. */
  dateRangeApplied: "الفترة المطبّقة:",

  columnGuard: "الحارس",
  columnType: "النوع",
  columnPeriod: "الفترة",
  columnFiled: "تاريخ الطلب",
  columnStatus: "الحالة",
  attachment: "مرفق",
  hasAttachment: "يوجد مرفق",
  noAttachment: "لا مرفق",
  fromNow: "من الآن",

  /* Detail */
  requestTitle: "طلب إجازة",
  backToQueue: "العودة إلى القائمة",
  period: "الفترة",
  duration: "المدة",
  notes: "ملاحظات الحارس",
  noNotes: "لا ملاحظات.",
  supervisor: "المشرف المسؤول",
  guard: "الحارس",

  /* The timeline the prototype serialised and never displayed. */
  timelineTitle: "سجل الطلب",
  timelineFiled: "قُدِّم الطلب",
  /* Shown instead of `timelineFiled` when somebody filed on the employee's
     behalf, so a leave day recorded against a guard never looks like one they
     asked for themselves. */
  timelineFiledOnBehalf: "سُجِّل الطلب نيابةً عن الموظف",
  timelineDecided: "صدر القرار",
  timelineReopened: "أُعيد فتح الطلب",
  timelineBy: "بواسطة",

  /* Leave already taken, as context for the decision — per type, since a
     category total spans allowances that have nothing to do with each other. */
  balanceTitle: "رصيد الإجازة",
  balanceType: "النوع",
  balanceRemaining: "المتبقي",
  balanceRequests: "عدد الطلبات",
  balanceNoLimit: "بدون حد",
  balanceRevoked: "لم يعد متاحًا",
  balanceOverdrawn: "تجاوز الرصيد",
  balanceEmpty: "لم يُحدَّد للموظف أي نوع إجازة بعد.",
  balanceHint: "المعتمدة فقط. الطلبات قيد المراجعة ليست حقيقة بعد.",

  /* Decision */
  decide: "القرار",
  approve: "اعتماد",
  reject: "رفض",
  approveTitle: "اعتماد الطلب",
  /* Approve is confirmed like reject. In the prototype it was one irreversible
     click while reject demanded a typed reason. */
  approveBody: "سيُعتمد الطلب ويصل إشعار للحارس. يمكن إعادة فتح القرار لاحقًا.",
  approveConfirm: "اعتماد",
  approveWithAttachmentTitle: "اعتماد مع مرفق",
  approveWithAttachmentBody:
    "نوع الإجازة هذا يتطلب مستندًا قبل الاعتماد. ارفع الإثبات الذي تبني عليه القرار، ثم أكّد.",
  approveAttachmentLabel: "المرفق",
  approveAttachmentHint: "PDF أو صورة، بحد أقصى ٨ ميجا.",
  approveAttachmentReady: "جاهز للإرفاق",
  approveAttachmentUploading: "جارٍ الرفع…",
  rejectTitle: "رفض الطلب",
  rejectReasonLabel: "سبب الرفض",
  rejectReasonHint: "يظهر للحارس. اذكر السبب بوضوح.",
  rejectConfirm: "رفض الطلب",
  reopen: "إعادة فتح القرار",
  reopenTitle: "إعادة فتح القرار",
  reopenBody: "سيعود الطلب إلى قائمة المراجعة، ويُسجَّل من أعاد فتحه ومتى.",
  reopenConfirm: "إعادة الفتح",

  /* Bulk */
  selectAll: "تحديد الكل",
  selected: "محدَّد",
  clearSelection: "إلغاء التحديد",
  bulkApprove: "اعتماد المحدَّد",
  bulkApproveTitle: "اعتماد الطلبات المحدَّدة",
  bulkApproveBody: "سيُعتمد كل طلب محدَّد ما زال قيد المراجعة، ويصل إشعار لكل حارس.",
  bulkApproveCount: "عدد الطلبات التي ستُعتمد:",
  bulkSkipped: "طلبات محدَّدة تم تجاوزها لأنها ليست قيد المراجعة:",
  bulkSkippedAttachment:
    "تم التجاوز — هذه الطلبات تتطلب مرفقًا؛ افتح كل طلب وارفع الإثبات واعتمد:",
  bulkNeedsAttachmentOnly:
    "لا يمكن اعتماد أي من الطلبات المحدَّدة بالجملة — كل طلب يتطلب مرفقًا. افتحها واحدًا تلو الآخر.",
  bulkApproveConfirm: "اعتماد",

  /* Outcomes */
  approved: "تم اعتماد الطلب.",
  reopened: "تم إعادة فتح الطلب.",
  bulkApproved: "تم اعتماد الطلبات.",

  emptyTitle: "لا طلبات",
  emptyLead: "طلبات الحراس تظهر هنا عند تقديمها من التطبيق.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل الفترة أو إزالة عوامل التصفية.",
  clearFilters: "إزالة التصفية",
  readOnlyNote: "لديك صلاحية العرض فقط على الطلبات.",

  errors: {
    /* The status guard the prototype's decision handler did not have: an
       approved request could be re-decided by curl, overwriting the decision. */
    reasonRequired: "اذكر سبب الرفض.",
    reasonTooLong: "السبب طويل جدًا.",
    noneSelected: "لم تحدد أي طلب.",
  },
} as const;

/** Momentary on-shift permissions (أذونات اللحظة) — separate from day-based leave. */
export const breaks = {
  title: "أذونات اللحظة",
  lead: "طلبات استراحة بمدة ثابتة: ١٥ أو ٣٠ أو ٦٠ دقيقة. لا يبدأ العدّ إلا بعد موافقة المشرف.",

  approved: "تمت الموافقة على الإذن.",
  rejected: "تم رفض الإذن.",
  reopened: "أُعيد فتح الطلب.",

  tabPending: "قيد المراجعة",
  tabApproved: "معتمدة",
  tabRejected: "مرفوضة",
  tabAll: "الكل",

  filterType: "النوع",
  filterGuard: "الحارس",

  columnGuard: "الحارس",
  columnType: "النوع",
  columnMinutes: "المدة (دقيقة)",
  columnWindow: "الفترة",
  columnFiled: "تاريخ الطلب",
  columnStatus: "الحالة",

  countsAsWorked: "الوقت محسوب من الدوام",
  doesNotCountAsWorked: "الوقت غير محسوب من الدوام",
  fromNow: "من الآن",
  startsOnApproval: "تبدأ عند موافقة المشرف",
  notStarted: "لم تبدأ",

  requestTitle: "طلب إذن",
  backToQueue: "العودة إلى القائمة",
  notes: "ملاحظات الحارس",
  noNotes: "لا ملاحظات.",
  supervisor: "المشرف المسؤول",
  guard: "الحارس",

  timelineTitle: "سجل الطلب",
  timelineFiled: "قُدِّم الطلب",
  timelineDecided: "صدر القرار",
  timelineReopened: "أُعيد فتح الطلب",
  timelineBy: "بواسطة",

  approve: "اعتماد",
  reject: "رفض",
  approveTitle: "اعتماد الطلب",
  approveBody: "ستبدأ الاستراحة الآن للمدة المعتمدة (١٥ أو ٣٠ أو ٦٠ دقيقة)، ويصل إشعار للحارس.",
  approveConfirm: "اعتماد",
  rejectTitle: "رفض الطلب",
  rejectReasonLabel: "سبب الرفض",
  rejectReasonHint: "يظهر للحارس. اذكر السبب بوضوح.",
  rejectConfirm: "رفض الطلب",
  reopen: "إعادة فتح القرار",
  reopenTitle: "إعادة فتح القرار",
  reopenBody: "سيعود الطلب إلى قائمة المراجعة، ويُسجَّل من أعاد فتحه ومتى.",
  reopenConfirm: "إعادة الفتح",

  emptyTitle: "لا أذونات",
  emptyLead: "طلبات الأذونات تظهر هنا عند تقديمها من التطبيق.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب إزالة عوامل التصفية.",
  clearFilters: "مسح التصفية",
  readOnlyNote: "لديك صلاحية العرض فقط على هذه الطلبات.",

  errors: {
    reasonRequired: "اذكر سبب الرفض.",
    reasonTooLong: "السبب طويل جدًا.",
  },
} as const;

/** The Attendance module: the punch log and its corrections. */
export const leaveTypes = {
  title: "أنواع الإجازات",
  lead: "كتالوج أنواع الإجازات — الأسماء والأيام الافتراضية وحالة التفعيل.",
  newTitle: "إضافة نوع إجازة",
  newLead: "عرّف الرمز والأسماء وقواعد الاستحقاق. الأنواع النشطة تُمنح في ملفات الموظفين.",
  editTitle: "تعديل نوع الإجازة",
  create: "إضافة النوع",
  createCta: "إضافة النوع",
  save: "حفظ التعديلات",
  saving: "جارٍ الحفظ…",
  cancel: "إلغاء",
  edit: "تعديل",

  nameAr: "الاسم بالعربية",
  nameEn: "الاسم بالإنجليزية",
  nameEnHint: "اختياري.",
  code: "الرمز",
  codeHint: "حروف صغيرة وأرقام وشرطات سفلية. لا يُغيَّر بعد الإنشاء.",
  codeImmutable: "الرمز ثابت بعد الإنشاء حتى تعتمد عليه التطبيقات والتكاملات.",
  group: "التصنيف",
  pay: "نوع الأجر",
  payPaid: "مدفوعة",
  payPartial: "مدفوعة جزئيًا",
  payUnpaid: "غير مدفوعة",
  payPercent: "نسبة الأجر (%)",
  payPercentHint: "مطلوب عندما يكون نوع الأجر جزئيًا (١–٩٩).",
  defaultDays: "الأيام الافتراضية",
  maxPerYear: "الحد الأقصى سنويًا",
  color: "اللون",
  colorHint: "اختياري. بصيغة #RRGGBB.",
  sortOrder: "ترتيب العرض",
  requiresAttachment: "يتطلب مرفقًا عند الاعتماد",
  requiresAttachmentHint:
    "إذا كان مفعّلًا، يجب على المشرف رفع مستند (مثل تقرير طبي) قبل اعتماد الطلب. لا يرفع الحارس شيئًا عند تقديم الطلب.",
  active: "نشط",

  columnName: "النوع",
  columnCode: "الرمز",
  columnGroup: "التصنيف",
  columnDays: "الأيام",
  columnStatus: "الحالة",
  columnActions: "إجراءات",
  daysUnset: "—",

  statusAll: "الكل",
  statusActive: "نشط",
  statusInactive: "متوقف",
  searchPlaceholder: "ابحث بالاسم أو الرمز",
  filterStatus: "الحالة",

  deactivateTitle: "إيقاف نوع الإجازة",
  deactivateBody:
    "لن يظهر النوع عند تسجيل طلبات جديدة. الطلبات السابقة ومنح الموظفين تبقى كما هي.",
  deactivateConfirm: "إيقاف",

  emptyTitle: "لا أنواع إجازات بعد",
  emptyLead: "أضف أول نوع ليصبح متاحًا في ملفات الموظفين والطلبات.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو إزالة عوامل التصفية.",
  clearFilters: "إزالة التصفية",
} as const;

/**
 * Security incident reports queue and type catalogue (Settings tab).
 */
export const incidents = {
  title: "البلاغات الأمنية",
  lead: "بلاغات الحراس من التطبيق — الحالة والموقع والمرفقات.",
  requestTitle: "تفاصيل البلاغ",
  backToQueue: "العودة للقائمة",

  tabNew: "جديد",
  tabUnderReview: "قيد المراجعة",
  tabResolved: "مغلق",
  tabRejected: "مرفوض",
  tabAll: "الكل",
  searchPlaceholder: "ابحث بالحارس أو الوصف",

  columnGuard: "الحارس",
  columnType: "نوع البلاغ",
  columnArea: "الموقع",
  columnStatus: "الحالة",
  columnSubmitted: "وقت الإرسال",
  columnAttachments: "مرفقات",

  statusNew: "جديد",
  statusUnderReview: "قيد المراجعة",
  statusResolved: "مغلق",
  statusRejected: "مرفوض",

  filterType: "نوع البلاغ",
  filterGuard: "الحارس",
  filterZone: "الموقع",
  filterFrom: "من تاريخ",
  filterTo: "إلى تاريخ",
  dateRangeHint: "يُفلتر حسب تاريخ إرسال البلاغ.",
  dateRangeApplied: "الفترة المطبّقة:",

  description: "الوصف",
  otherLabel: "تفاصيل أخرى",
  location: "موقع الإرسال",
  noLocation: "لا توجد إحداثيات مسجّلة مع البلاغ.",
  attachments: "المرفقات",
  noAttachments: "لا توجد مرفقات.",
  adminNotes: "ملاحظات الإدارة",
  adminNotesHint: "تظهر للحارس لما تتحدّث حالة البلاغ.",
  updateStatus: "تحديث الحالة",
  updating: "جارٍ التحديث…",
  statusUpdated: "تم تحديث حالة البلاغ.",
  reviewedBy: "راجعها",
  reviewedAt: "وقت المراجعة",
  supervisor: "المشرف",

  emptyTitle: "لا بلاغات",
  emptyLead: "البلاغات تظهر هنا عند إرسالها من تطبيق الحارس.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو إزالة عوامل التصفية.",
  clearFilters: "إزالة التصفية",
} as const;

export const incidentTypes = {
  title: "أنواع البلاغات الأمنية",
  lead: "القائمة التي يختار منها الحارس في التطبيق. عطِّل النوع إن لم تعد تحتاجونه.",
  newTitle: "إضافة نوع بلاغ",
  newLead: "عرّف الرمز والأسماء. الأنواع النشطة تظهر في قائمة التطبيق.",
  editTitle: "تعديل نوع البلاغ",
  create: "إضافة النوع",
  createCta: "إضافة النوع",
  save: "حفظ التعديلات",
  saving: "جارٍ الحفظ…",
  cancel: "إلغاء",
  edit: "تعديل",

  nameAr: "الاسم بالعربية",
  nameEn: "الاسم بالإنجليزية",
  nameEnHint: "اختياري.",
  code: "الرمز",
  codeHint: "حروف صغيرة وأرقام وشرطات سفلية. لا يُغيَّر بعد الإنشاء.",
  codeImmutable: "الرمز ثابت بعد الإنشاء حتى تعتمد عليه التطبيقات.",
  color: "اللون",
  colorHint: "اختياري. بصيغة #RRGGBB.",
  sortOrder: "ترتيب العرض",
  sensitive: "حساس (تنبيه أقوى للمشرف)",
  sensitiveHint: "عندما يرسل الحارس بلاغًا من هذا النوع، يتلقى المشرف تنبيهًا أوضح.",
  allowsOtherText: "يسمح بنص حر (مثل «أخرى»)",
  allowsOtherTextHint: "إذا كان مفعّلًا، يجب على الحارس كتابة وصف قصير إضافي مع النوع.",
  active: "نشط",

  columnName: "النوع",
  columnCode: "الرمز",
  columnSensitive: "حساس",
  columnOther: "نص حر",
  columnStatus: "الحالة",
  columnActions: "إجراءات",

  statusAll: "الكل",
  statusActive: "نشط",
  statusInactive: "متوقف",
  searchPlaceholder: "ابحث بالاسم أو الرمز",
  filterStatus: "الحالة",

  deactivateTitle: "إيقاف نوع البلاغ",
  deactivateBody: "لن يظهر النوع في قائمة التطبيق. البلاغات السابقة تبقى كما هي.",
  deactivateConfirm: "إيقاف",

  emptyTitle: "لا أنواع بلاغات بعد",
  emptyLead: "أضف أول نوع ليصبح متاحًا في التطبيق.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو إزالة عوامل التصفية.",
} as const;

/**
 * Queue for mobile single-device rebinds. Admin only — approving swaps the
 * bound handset id on the account.
 */
export const deviceChanges = {
  title: "تغيير الجوال أو الجهاز",
  lead: "طلبات من فقد رقمه المسجّل أو بدّل جواله. تأكّد من هوية صاحب الطلب قبل الاعتماد؛ الرقم يستقبل رموز استعادة الحساب، والجهاز هو الوحيد الذي يُقبل منه الحضور.",

  tabPhone: "رقم الجوال",
  tabDevice: "الجهاز",
  tabPending: "قيد المراجعة",
  tabApproved: "معتمدة",
  tabRejected: "مرفوضة",
  tabAll: "الكل",
  searchPlaceholder: "ابحث بالاسم أو الرقم الوظيفي…",

  columnRequester: "صاحب الطلب",
  columnNumber: "الرقم",
  columnAction: "الإجراء",
  idSuffix: "الهوية تنتهي بـ",
  currentNumber: "الحالي",
  newNumber: "الجديد",
  decidedBy: "قرّره",
  approveNumber: "اعتماد الرقم",
  supervisorNote: "مشرف — يعتمده فريق العمليات",
  columnRole: "الدور",
  columnPhone: "الجوال",
  columnPreviousDevice: "الجهاز الحالي",
  columnNewDevice: "الجهاز الجديد",
  columnFiled: "تاريخ الطلب",
  columnStatus: "الحالة",
  columnReason: "السبب",

  noPreviousDevice: "—",
  noPhone: "—",
  noReason: "لم يُذكر سبب.",

  requestTitle: "طلب تغيير جهاز",
  backToQueue: "العودة إلى القائمة",

  timelineTitle: "سجل الطلب",
  timelineFiled: "قُدِّم الطلب",
  timelineDecided: "صدر القرار",
  timelineBy: "بواسطة",

  approve: "اعتماد",
  reject: "رفض",
  approveTitle: "اعتماد الرقم الجديد",
  approveBody:
    "تأكدت من هوية صاحب الطلب؟ يستقبل الرقم الجديد رموز استعادة الحساب، ولن يستقبلها الرقم القديم بعد الآن. يصل الرد برسالة نصية إلى الرقم الجديد.",
  approveConfirm: "اعتماد الرقم",
  rejectTitle: "رفض",
  rejectReasonLabel: "سبب الرفض",
  rejectReasonHint: "يصل إلى الرقم الجديد برسالة نصية.",
  rejectConfirm: "رفض",

  approved: "تم اعتماد تغيير الجهاز.",
  rejected: "تم رفض طلب تغيير الجهاز.",

  emptyTitle: "لا طلبات",
  emptyLead: "تظهر الطلبات هنا عندما يقدّمها صاحب الحساب من التطبيق.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو إزالة عوامل التصفية.",
  clearFilters: "إزالة التصفية",
  readOnlyNote: "لديك صلاحية العرض فقط على هذه الطلبات.",
} as const;

export const attendance = {
  title: "الحضور",
  lead: "سجل تسجيلات الحضور والانصراف، وتصحيحها عند الحاجة.",

  columnGuard: "الحارس",
  columnType: "النوع",
  columnTime: "الوقت",
  columnZone: "المنطقة",
  columnStatus: "حالة النطاق",
  columnSource: "المصدر",
  columnLate: "التأخير",
  columnAccuracy: "دقة الموقع",

  checkIn: "حضور",
  checkOut: "انصراف",
  sourceMobile: "التطبيق",
  sourceManual: "تصحيح يدوي",
  sourceKiosk: "جهاز الموقع",
  insideZone: "داخل المنطقة",
  outsideZone: "خارج المنطقة",
  noPosition: "بلا موقع",
  noZone: "بدون منطقة",
  onTime: "في الموعد",
  unscheduled: "بلا مناوبة مجدولة",

  searchPlaceholder: "ابحث باسم الحارس",
  filterType: "النوع",
  filterStatus: "حالة النطاق",
  filterSource: "المصدر",
  filterGuard: "الحارس",
  filterZone: "المنطقة",
  filterFrom: "من تاريخ",
  filterTo: "إلى تاريخ",
  dateRangeApplied: "الفترة المطبّقة:",

  /* The exceptions queue. The prototype read "on duty" from the last attendance
     row ever recorded, with no date bound, and dropped the unclosed punch from
     duty hours — so this state was both invisible and silently wrong. */
  unclosedTitle: "تسجيلات حضور غير مغلقة",
  unclosedLead: "حضور بلا انصراف مقابل. يظهر هنا بعد ١٢ ساعة من التسجيل.",
  unclosedSince: "مفتوح منذ",
  hours: "ساعة",

  /* Manual correction */
  correct: "تصحيح يدوي",
  correctTitle: "تسجيل يدوي",
  correctLead: "لتسجيل حضور أو انصراف لم يُسجَّل من التطبيق. يُسجَّل باسمك ومع السبب.",
  correctGuard: "الحارس",
  correctType: "النوع",
  correctAt: "الوقت",
  correctAtHint: "بتوقيت الرياض.",
  correctReason: "سبب التصحيح",
  correctReasonHint: "مطلوب. يظهر في سجل الحضور وفي سجل التغييرات.",
  correctSubmit: "تسجيل",
  corrected: "تم تسجيل التصحيح.",
  correctedBy: "صُحّح بواسطة",
  closeUnclosed: "تسجيل انصراف",

  emptyTitle: "لا تسجيلات",
  emptyLead: "تسجيلات الحضور تصل من تطبيق الحراس.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل الفترة أو إزالة عوامل التصفية.",
  clearFilters: "إزالة التصفية",
  readOnlyNote: "لديك صلاحية العرض فقط على سجل الحضور.",

  errors: {
    guardRequired: "اختر الحارس.",
    typeInvalid: "اختر نوع التسجيل.",
    timeInvalid: "الوقت غير صحيح.",
    reasonRequired: "اذكر سبب التصحيح.",
    reasonTooLong: "السبب طويل جدًا.",
  },
} as const;

/** Live tracking. */
export const tracking = {
  title: "التتبع المباشر",
  lead: "مواقع الحراس على المخطط، مع حالة الاتصال ومخالفات النطاق.",

  /* Three states, not two. "No signal" is the guard the prototype passed to the
     map as {lat: null, lng: null}. */
  onDuty: "على رأس العمل",
  offDuty: "خارج الدوام",
  noSignal: "لا توجد إشارة",
  rosterTitle: "الحراس",
  rosterEmpty: "لا حراس نشطون.",
  neverPinged: "لم يُرسل أي موقع",
  lastSeen: "آخر إشارة",
  stale: "إشارة متوقفة",
  outsideZone: "خارج مناطقه",
  onBreak: "في استراحة",
  workedTime: "وقت العمل",
  accuracy: "الدقة",
  focus: "تحديد على المخطط",
  clearFocus: "إلغاء التحديد",

  /* The live-update control. Clicking pauses or resumes the stream + fallback
     poll; the label reflects the *operator's* choice, not only the socket. */
  connected: "التحديث المباشر يعمل",
  connecting: "جارٍ الاتصال…",
  disconnected: "التحديث المباشر متوقف",
  liveToggle: "تشغيل أو إيقاف التحديث المباشر",
  refresh: "تحديث الآن",
  updatedAt: "آخر تحديث",
  liveHint: "اضغط على حالة التحديث لإيقافه أو تشغيله. المواقع تُحدَّث تلقائيًا كل ٣٠ ثانية وعند كل إشارة جديدة.",

  toastBreakExceeded: "حارس تجاوز مدة الاستراحة المسموح بها",
  toastAccountSuspended: "تم إيقاف حساب حارس لتجاوز الاستراحة",
  toastOutOfZone: "حارس خارج النطاق بدون إذن",

  /* Trail playback: the read the locations table never had. */
  trailTitle: "مسار الحارس",
  trailPoints: "عدد النقاط",
  trailEmpty: "لا نقاط مسجَّلة في هذه الفترة.",
  showTrail: "عرض المسار",
  hideTrail: "إخفاء المسار",

} as const;

/** Reports. */
export const reports = {
  title: "التقارير",
  lead: "ساعات الدوام والالتزام بالمواعيد، محسوبة بتوقيت الرياض على الجدول المنشور.",

  /* Range */
  rangeFrom: "من تاريخ",
  rangeTo: "إلى تاريخ",
  apply: "تطبيق",
  presetToday: "اليوم",
  preset7: "٧ أيام",
  preset30: "٣٠ يومًا",
  presetThisMonth: "هذا الشهر",
  presetLastMonth: "الشهر الماضي",
  rangeApplied: "الفترة:",
  /* The prototype accepted from > to and returned an empty result that looked
     exactly like a quiet period. */
  rangeSwapped: "تم تصحيح ترتيب التاريخين.",

  filterGuard: "الحارس",
  filterZone: "المنطقة",
  filterSupervisor: "المشرف",

  /* KPI row */
  kpiPunctuality: "الالتزام بالمواعيد",
  kpiCoverage: "تغطية الساعات",
  kpiWorked: "ساعات الدوام",
  kpiScheduled: "الساعات المجدولة",
  kpiLate: "دقائق التأخير",
  kpiAbsences: "الغياب",
  kpiOutside: "مخالفات النطاق",
  kpiUnclosed: "تسجيلات غير مغلقة",
  noData: "لا بيانات في هذه الفترة",

  /* Charts */
  trendTitle: "الالتزام بالمواعيد عبر الوقت",
  trendLead: "نسبة الحضور في الموعد لكل يوم. الأيام بلا مناوبات مجدولة تظهر فارغة، لا صفرًا.",
  trendAxisRate: "النسبة",
  hoursByZoneTitle: "الساعات المجدولة حسب المنطقة",
  leaveMixTitle: "توزيع الإجازات المعتمدة",
  leaveMixDays: "أيام",
  leaveMixType: "النوع",
  punctualityByZoneTitle: "الالتزام بالمواعيد حسب المنطقة",
  punctualityByZoneLead:
    "أدنى المناطق التزامًا تحمل اللون البارز والباقي يتراجع — السؤال هنا «أي موقع هو المشكلة»، لا مقارنة الجميع بالجميع.",
  scheduledPunches: "تسجيلات مجدولة",
  coverageTitle: "تغطية الجدول",
  coverageLead: "الساعات المعمولة فعلًا من الساعات التي طلبها الجدول في هذه الفترة.",
  coverageMeterLabel: "نسبة التغطية",
  ofScheduled: "من أصل",
  heatmapTitle: "توزيع التسجيلات — الأيام والساعات",
  heatmapLead:
    "متى يحدث العمل فعلًا: عدد تسجيلات الحضور والانصراف لكل ساعة من كل يوم أسبوع، محسوبًا بتوقيت المنشأة. الخلية الأغمق أكثر ازدحامًا، والفارغة بلا تسجيلات أصلًا.",
  chartEmpty: "لا بيانات لرسمها في هذه الفترة.",
  chartTarget: "المستهدف",
  ofTotal: "من",

  /* Table */
  columnGuard: "الحارس",
  columnSupervisor: "المشرف",
  columnScheduled: "المجدول",
  columnWorked: "الفعلي",
  columnLate: "التأخير",
  columnEarly: "الخروج المبكر",
  columnPunctuality: "الالتزام",
  columnAbsences: "الغياب",
  columnLeave: "الإجازات",
  columnOutside: "خارج النطاق",
  notScheduled: "غير مجدول",

  /* Export and print */
  exportCsv: "تصدير CSV",
  print: "طباعة",
  /* Excel needs a byte-order mark to read UTF-8 Arabic; without it the file
     opens as mojibake. Worth stating, because it is the commonest defect in an
     Arabic CSV export. */
  exportNote: "الملف بترميز UTF-8 وبأرقام إنجليزية، ليُقرأ في Excel وجداول البيانات.",
  printedAt: "تاريخ الطباعة",

  emptyTitle: "لا بيانات في هذه الفترة",
  emptyLead: "جرّب فترة أوسع، أو تحقق من نشر جدول المناوبات — التقارير تُقاس على الجدول المنشور.",
} as const;

/** Organisation settings, and the operator's own account. */
export const settings = {
  title: "الإعدادات",
  lead: "إعدادات المنشأة وحسابك الشخصي.",

  tabOrg: "المنشأة",
  tabProfile: "حسابي",
  tabSecurity: "الأمان",
  tabReportTypes: "أنواع البلاغات",

  /* Organisation — every one of these was a literal in the prototype's source. */
  orgTitle: "إعدادات المنشأة",
  orgLead: "تسري على كل المستخدمين، وتُقاس عليها الحسابات في التقارير.",
  timeZone: "المنطقة الزمنية",
  timeZoneHint: "كل الحسابات — المناوبات والتأخير والتقارير — تُحسب بها.",
  mapCentre: "مركز الخريطة الافتراضي",
  mapCentreHint: "حيث تفتح الخريطة حين لا توجد حدود مرسومة بعد.",
  mapLat: "خط العرض",
  mapLng: "خط الطول",
  defaultPolicy: "سياسة النطاق الافتراضية",
  defaultPolicyHint: "تُطبَّق على منطقة جديدة، وعلى حارس غير معيَّن على أي منطقة.",
  staleMinutes: "دقائق حتى تُعد الإشارة متوقفة",
  staleMinutesHint: "بعدها يظهر الحارس بلا إشارة على لوحة التتبع.",
  retentionDays: "أيام الاحتفاظ بمواقع الحراس",
  retentionDaysHint: "تُحذف نقاط المسار الأقدم من ذلك. سجل الحضور لا يُحذف.",
  inviteExpiry: "ساعات صلاحية روابط التنشيط",
  minPassword: "أقل عدد لأحرف كلمة المرور",
  maxActiveEmployees: "أقصى عدد للحسابات النشطة",
  maxActiveEmployeesHint:
    "يُستخدم للشركات التي لم تُحفظ لها الحدود الثلاثة بعد، ولمن لا يتبع شركة. اتركها فارغة لعدم وجود حد.",
  maxSupervisors: "الحد الافتراضي للمشرفين",
  maxSupervisorsHint: "يُنسخ إلى شركة جديدة عند ترك حقل المشرفين فارغًا. اتركه فارغًا لعدم وجود حد.",
  maxGuards: "الحد الافتراضي للحراس",
  maxGuardsHint: "يُنسخ إلى شركة جديدة عند ترك حقل الحراس فارغًا. اتركه فارغًا لعدم وجود حد.",
  maxAreas: "الحد الافتراضي للمناطق",
  maxAreasHint: "يُنسخ إلى شركة جديدة عند ترك حقل المناطق فارغًا. اتركه فارغًا لعدم وجود حد.",
  notifyBreach: "إشعار عند الخروج من النطاق",
  notifyBreachHint: "يصل للمشرف المسؤول عن الحارس.",
  saveOrg: "حفظ الإعدادات",
  orgSaved: "تم حفظ الإعدادات.",

  /* Profile */
  profileTitle: "حسابي",
  name: "الاسم",
  phone: "رقم الجوال",
  email: "البريد الإلكتروني",
  emailReadOnly: "البريد لا يُغيَّر من هنا. راجع مدير النظام.",
  language: "لغة الواجهة",
  languageArabic: "العربية",
  languageEnglish: "English",
  languageHint: "تسري على القوائم والنماذج ورسائل النظام في هذه الجلسة.",
  numerals: "نظام الأرقام المعروضة",
  numeralsArabic: "أرقام عربية (٠١٢٣)",
  numeralsWestern: "أرقام إنجليزية (0123)",
  numeralsHint: "يسري على الجداول والتقارير والرسوم. التصدير يبقى بأرقام إنجليزية دائمًا.",
  saveProfile: "حفظ",
  profileSaved: "تم حفظ بياناتك.",

  /* Security */
  securityTitle: "الأمان",
  changePassword: "تغيير كلمة المرور",
  currentPassword: "كلمة المرور الحالية",
  newPassword: "كلمة المرور الجديدة",
  confirmPassword: "تأكيد كلمة المرور",
  passwordSaved: "تم تغيير كلمة المرور.",
  sessionsTitle: "الجلسات النشطة",
  sessionsLead: "كل جهاز سجّل الدخول بحسابك. تشمل تطبيق الحراس إن كنت تستخدمه.",
  sessionCurrent: "هذه الجلسة",
  sessionLastSeen: "آخر نشاط",
  sessionSignOut: "إنهاء",
  signOutOthers: "إنهاء الجلسات الأخرى",
  signOutOthersTitle: "إنهاء الجلسات الأخرى",
  signOutOthersBody: "ستُنتهى كل الجلسات ما عدا الحالية. ستحتاج تسجيل الدخول من جديد على الأجهزة الأخرى.",
  signOutOthersConfirm: "إنهاء",
  sessionsRevoked: "تم إنهاء الجلسات الأخرى.",
  sessionRevoked: "تم إنهاء الجلسة.",
  sessionsEmpty: "لا جلسات أخرى.",

  readOnlyNote: "الإعدادات العامة من صلاحيات مدير النظام. يمكنك تعديل حسابك وأمانه.",

  errors: {
    generic: "تعذّر إكمال العملية. أعد المحاولة.",
  },
} as const;

/**
 * Notifications.
 *
 * The prototype stored a pre-rendered Arabic sentence in a `message` column, so
 * every notification was untranslatable — a user who set their language to
 * English still received Arabic. Here the row stores a type and a payload, and
 * the sentence is built from this catalogue at display time.
 *
 * @dynamic — reached as `notificationText[row.type]`.
 */
export const notificationText = {
  "leave_request:new": "طلب إجازة جديد",
  "leave_request:decided": "تم البت في طلب إجازتك",
  "leave_request:reopened": "أُعيد فتح طلب إجازة",
  "geofence:breach": "تسجيل حضور خارج النطاق",
  "shift:coverage_gap": "نقص في تغطية مناوبة",
  "shift:published": "تم نشر جدول المناوبات",
  "attendance:unclosed": "تسجيل حضور غير مغلق",
  "attendance:break_exceeded": "تجاوز مدة الاستراحة المسموح بها",
  "attendance:account_suspended": "تم إيقاف الحساب لتجاوز الاستراحة",
  "account:invited": "تم إنشاء حسابك",
  "hr:expiry": "انتهاء مستند أو عقد موظف",
  "company:contract_expiry": "اقتراب انتهاء عقد شركة",
  "device_change:decided": "تم البت في طلب تغيير جهاز الدخول",
  "break_request:new": "طلب إذن جديد",
  "break_request:decided": "تم البت في طلب إذنك",
} as const;

export const notifications = {
  title: "الإشعارات",
  lead: "السجل كامل، لا غير المقروء فقط.",

  tabAll: "الكل",
  tabUnread: "غير المقروء",
  /* The toggle is labelled by what clicking gives, not by the current state. */
  sortOldestFirst: "الأقدم أولًا",
  sortNewestFirst: "الأحدث أولًا",
  markAllRead: "تحديد الكل كمقروء",
  markRead: "تحديد كمقروء",
  allRead: "تم تحديد الكل كمقروء.",
  unreadBadge: "جديد",
  open: "فتح",
  /* Its notifications were dead ends: clicking one marked it read and removed it
     from the list, and the `referenceId` it serialised for exactly this purpose
     was used by nothing. */
  emptyTitle: "لا إشعارات",
  emptyLead: "الإشعارات تظهر هنا عند وصول طلب إجازة أو نشر جدول أو مخالفة نطاق.",
  emptyUnreadTitle: "لا إشعارات غير مقروءة",
  emptyUnreadLead: "كل الإشعارات مقروءة.",
} as const;

/** The audit log. */
export const audit = {
  title: "سجل التغييرات",
  lead: "من غيّر ماذا ومتى، ومن أي عنوان.",

  columnWhen: "الوقت",
  columnActor: "المنفّذ",
  columnAction: "الإجراء",
  columnEntity: "الكيان",
  columnChange: "التغيير",
  columnIp: "عنوان IP",

  filterActor: "المنفّذ",
  filterAction: "الإجراء",
  filterEntity: "الكيان",
  searchPlaceholder: "ابحث في الكيان",

  actionCreate: "إضافة",
  actionUpdate: "تعديل",
  actionDelete: "حذف أو أرشفة",
  actionRestore: "إعادة تنشيط",

  system: "النظام",
  before: "قبل",
  after: "بعد",
  noChange: "لا تفاصيل مسجّلة.",
  showChange: "عرض التغيير",

  emptyTitle: "لا تغييرات مسجّلة",
  emptyLead: "كل عملية كتابة في المنصة تُسجَّل هنا.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب إزالة بعض عوامل التصفية.",
  clearFilters: "إزالة التصفية",
} as const;

/** The display-theme choice, in the top bar. */
export const theme = {
  label: "المظهر",
  paper: "فاتح",
  navy: "داكن",
} as const;

/** Table paging controls. */
export const pagination = {
  page: "صفحة",
  of: "من",
  none: "لا نتائج",
  previous: "الصفحة السابقة",
  next: "الصفحة التالية",
  first: "الصفحة الأولى",
  last: "الصفحة الأخيرة",
  perPage: "عدد الصفوف",
} as const;

/** The shared filter bar. */
export const filters = {
  all: "الكل",
  clear: "إزالة التصفية",
  apply: "تطبيق",
  updating: "جارٍ التحديث",
} as const;

/**
 * The command palette.
 *
 * Opened with Ctrl+K, or Cmd+K on a Mac. The hint names both, because the console
 * is used on Windows workstations and on a manager's laptop and guessing wrong
 * makes the affordance invisible to half the users.
 */
export const palette = {
  open: "بحث وتنقل",
  shortcutHint: "Ctrl K",
  placeholder: "ابحث عن موظف أو منطقة، أو انتقل لشاشة…",
  sectionPages: "الشاشات",
  sectionEmployees: "الموظفون",
  sectionZones: "المناطق",
  searching: "جارٍ البحث…",
  /* Said rather than left blank: an empty panel reads as broken, and the reason
     is a real rule — two letters, because one letter matches everything. */
  typeMore: "اكتب حرفين على الأقل للبحث في الموظفين والمناطق.",
  noResults: "لا نتائج مطابقة.",
  inactiveTag: "موقوف",
  hintNavigate: "للتنقل",
  hintSelect: "للفتح",
  hintClose: "للإغلاق",
} as const;

/**
 * The 404.
 *
 * The prototype redirected any unknown URL to `/`, which bounced again by role —
 * so a mistyped address gave no feedback and looked exactly like being signed
 * out. Its own group because the page renders on both sides of the session
 * boundary and belongs to neither.
 */
export const notFound = {
  title: "الصفحة غير موجودة",
  lead: "الرابط الذي وصلت منه غير صحيح أو قديم. إذا وصلك من داخل المنصة، أخبرنا.",
  backToConsole: "رجوع للمنصة",
} as const;

/** Loading and failure states shared by every screen that reads. */
export const queryState = {
  errorTitle: "تعذّر تحميل البيانات",
  errorLead: "لم تستجب الخدمة للطلب. جرّب تحديث الصفحة؛ إذا استمرت الحالة فأخبرنا.",
  retry: "إعادة المحاولة",
} as const;

/** A record that does not exist, shown inside the console shell. */
export const recordNotFound = {
  title: "السجل غير موجود",
  lead: "الرقم في الرابط بلا سجل — إما أنه تغيّر أو أُزيل. راجع القائمة.",
} as const;

/** The shared confirmation dialog. */
export const confirm = {
  cancel: "إلغاء",
  working: "جارٍ التنفيذ…",
} as const;

/**
 * Scheduled app maintenance windows.
 */
export const maintenance = {
  title: "الصيانة",
  lead: "جدولة نوافذ صيانة التطبيق لكل المنصات.",
  createCta: "إضافة صيانة",

  columnPlatforms: "المنصة",
  columnStartsAt: "من",
  columnEndsAt: "إلى",
  columnResponsible: "المسؤول",
  columnReason: "السبب",
  columnStatus: "الحالة",
  columnActions: "إجراءات",

  statusScheduled: "مجدولة",
  statusActive: "نشطة",
  statusEnded: "منتهية",
  statusInactive: "موقوفة",

  platformAndroid: "Android",
  platformIos: "iOS",
  platformAppGallery: "App Gallery",
  platformWeb: "Web",

  emptyTitle: "لا نوافذ صيانة",
  emptyLead: "أضف نافذة صيانة لإبلاغ التطبيقات عند توقف الخدمة.",

  formCreateTitle: "إضافة صيانة",
  formEditTitle: "تعديل الصيانة",
  formViewTitle: "عرض الصيانة",
  startsAt: "بداية الصيانة",
  endsAt: "نهاية الصيانة",
  platforms: "المنصات",
  platformsHint: "تُطبَّق هذه النافذة على كل المنصات المحددة.",
  minimumSupportedVersion: "أدنى إصدار مدعوم",
  currentVersion: "الإصدار الحالي",
  reasonAr: "سبب الصيانة (عربي)",
  reasonEn: "سبب الصيانة (إنجليزي)",
  messageAr: "رسالة الصيانة (عربي)",
  messageEn: "رسالة الصيانة (إنجليزي)",
  isActive: "مفعّلة",
  isActiveHint: "النوافذ الموقوفة لا تظهر للتطبيقات حتى لو كان التوقيت حاليًا.",
  responsible: "المسؤول",
  responsibleHint: "يُسجَّل تلقائيًا باسم منشئ السجل.",
  save: "حفظ",
  saving: "جارٍ الحفظ…",
  cancel: "إلغاء",
  edit: "تعديل",
  view: "عرض",
  delete: "حذف",
  deleteTitle: "حذف نافذة الصيانة؟",
  deleteBody: "سيُحذف السجل نهائيًا ولن يظهر للتطبيقات.",
  deleteConfirm: "حذف",
  deactivateTitle: "إيقاف نافذة الصيانة؟",
  deactivateBody: "لن تظهر للتطبيقات حتى تُفعَّل من جديد.",
  deactivateConfirm: "إيقاف",
  backToList: "العودة إلى الصيانة",
} as const;

/**
 * App version / store update records (one platform per row).
 */
export const updates = {
  title: "التحديثات",
  lead: "نشر إصدارات التطبيق لكل منصة على حدة.",
  createCta: "إضافة تحديث",

  columnPlatform: "المنصة",
  columnUpdateType: "نوع التحديث",
  columnCurrentVersion: "الإصدار الحالي",
  columnResponsible: "المسؤول",
  columnReason: "سبب التحديث",
  columnUpdateDate: "تاريخ التحديث",
  columnActions: "إجراءات",

  typeOptional: "اختياري",
  typeForced: "إجباري",

  platformAndroid: "Android",
  platformIos: "iOS",
  platformAppGallery: "App Gallery",
  platformWeb: "Web",

  emptyTitle: "لا سجلات تحديث",
  emptyLead: "أضف سجل تحديث لإبلاغ التطبيقات بالإصدار الجديد.",

  formCreateTitle: "إضافة تحديث",
  formEditTitle: "تعديل التحديث",
  formViewTitle: "عرض التحديث",
  platform: "المنصة",
  platformHint: "منصة واحدة لكل سجل — أنشئ سجلات منفصلة لـ Android و iOS وغيرهما.",
  updateType: "نوع التحديث",
  minimumSupportedVersion: "أدنى إصدار مدعوم",
  currentVersion: "الإصدار الحالي",
  reasonAr: "سبب التحديث (عربي)",
  reasonEn: "سبب التحديث (إنجليزي)",
  responsible: "المسؤول",
  responsibleHint: "يُسجَّل تلقائيًا باسم منشئ السجل.",
  save: "حفظ",
  saving: "جارٍ الحفظ…",
  cancel: "إلغاء",
  edit: "تعديل",
  view: "عرض",
  delete: "حذف",
  deleteTitle: "حذف سجل التحديث؟",
  deleteBody: "سيُحذف السجل نهائيًا ولن يظهر للتطبيقات.",
  deleteConfirm: "حذف",
  backToList: "العودة إلى التحديثات",
} as const;

/**
 * Bilingual policy pages shown in the app and on the public policy route.
 */
export const policies = {
  title: "السياسات",
  lead: "يظهر المحتوى المحفوظ هنا في التطبيق والموقع حسب لغة المستخدم.",
  add: "إضافة سياسة",
  published: "منشورة",
  hidden: "مخفية",
  publishedHint: "تظهر في التطبيق والموقع.",
  arabic: "العربية",
  english: "الإنجليزية",
  unsaved: "تغييرات غير محفوظة",
  save: "حفظ",
  saving: "جارٍ الحفظ…",
  edit: "تعديل",
  delete: "حذف",
  preview: "معاينة",
  deleteTitle: "حذف السياسة؟",
  deleteBody: "ستُحذف «{name}» ومحتواها بالعربية والإنجليزية. لا يمكن التراجع عن هذا الإجراء.",
  deleteConfirm: "حذف",
  cancel: "إلغاء",
  addTitle: "إضافة سياسة",
  addLead: "يظهر اسم السياسة للمستخدم حسب لغة التطبيق المختارة.",
  nameAr: "اسم السياسة (عربي)",
  nameEn: "اسم السياسة (إنجليزي)",
  nameArPlaceholder: "مثال: سياسة الشحن",
  nameEnPlaceholder: "e.g. Shipping Policy",
  permanentLink: "الرابط الدائم",
  slugDerivedHint: "يُشتق من الاسم الإنجليزي، ويبقى ثابتًا بعد الإنشاء.",
  slugFixedHint: "لا يتغير عند تعديل الاسم، حتى لا تنكسر الروابط التي تمت مشاركتها.",
  editTitle: "تعديل بيانات السياسة",
  saveChanges: "حفظ التغييرات",
  unsavedTitle: "تغييرات غير محفوظة",
  unsavedBody: "لديك تغييرات لم تُحفظ في «{name}». إذا تابعت، ستفقد هذه التغييرات.",
  discard: "تجاهل التغييرات",
  saveAndContinue: "حفظ ومتابعة",
  emptyTitle: "لا توجد سياسات",
  emptyLead: "أضف سياسة ليقرأها المستخدم في التطبيق.",
  lastUpdate: "آخر تحديث: {date}",
  notFoundTitle: "السياسة غير متاحة",
  notFoundLead: "هذا الرابط لا يطابق سياسة منشورة.",

  errors: {
    titleRequired: "اسم السياسة مطلوب.",
    titleTooLong: "اسم السياسة طويل جدًا.",
    contentTooLong: "المحتوى طويل جدًا. الصق نصًا عاديًا أو خفّف التنسيق.",
  },
} as const;

/**
 * The client companies — the counterparties Teslm guards sites for.
 *
 * Wording note: "شركة عميلة" throughout, never just "شركة". The operator's own
 * employer is also a company, and an admin reading "إيقاف الشركة" on a
 * destructive-looking control should be in no doubt which one is meant.
 */
export const companies = {
  title: "الشركات العميلة",
  lead: "الجهات التي نحرس مواقعها، ومن يتابعها من فريقنا.",
  newTitle: "إضافة شركة عميلة",
  newLead: "تُنشأ الشركة بحساب دخول واحد يُرسل إليه رابط التفعيل.",
  editTitle: "تعديل بيانات الشركة",
  create: "إضافة الشركة",
  save: "حفظ التعديلات",
  saving: "جارٍ الحفظ…",
  cancel: "إلغاء",

  /* Fields */
  nameAr: "الاسم بالعربي",
  nameEn: "الاسم بالإنجليزي",
  nameEnHint: "اختياري. لا تُعرض في الكونسول بعد.",
  commercialRegistration: "السجل التجاري",
  commercialRegistrationHint: "١٠ أرقام. الشرطات والمسافات تُزال تلقائيًا.",
  vatNumber: "الرقم الضريبي",
  vatNumberHint: "اختياري — إن وجد. ١٥ رقمًا.",
  bankAccount: "الحساب البنكي (IBAN)",
  bankAccountHint: "اختياري. يُحوّل إلى حروف كبيرة بدون مسافات.",
  establishmentNumber: "الرقم الموحّد للمنشأة",
  establishmentNumberHint: "اختياري. مطلوب لتصدير ملف حماية الأجور.",
  limitsHeading: "حدود الشركة",
  limitsIntroCreate:
    "أقصى عدد من المشرفين والحراس النشطين والمناطق. الخانة الفارغة تأخذ الحد العام من إعدادات النظام.",
  limitsIntroEdit:
    "يسري أي تغيير فور الحفظ. الخانة الفارغة تعني عدم تطبيق حد. خفض الحد دون الاستخدام الحالي لا يوقف أحدًا، ويمنع الإضافات الجديدة فقط.",
  limitBelowUsage: "أقل من الاستخدام الحالي ({used}). لن يُوقف أحد، وستُمنع الإضافات حتى ينخفض العدد.",
  limitSupervisors: "المشرفون",
  limitGuards: "الحراس",
  limitAreas: "المناطق",
  notes: "ملاحظات",
  notesHint: "اختياري. لا تُعرض للشركة.",
  email: "بريد الدخول",
  emailHint: "تصل عليه رسالة تعيين كلمة المرور، وتدخل به الشركة على بياناتها.",
  emailImmutable: "بريد الدخول لا يُعدّل من هنا. أضف حسابًا آخر للشركة إذا لزم.",

  /* Columns */
  columnName: "الشركة",
  columnSupervisors: "المشرفون",
  columnGuards: "الحراس",
  columnAreas: "المناطق",
  columnAccount: "حساب العميل",
  columnStatus: "الحالة",
  columnCreated: "تاريخ الإضافة",
  columnPerson: "الاسم",
  columnSupervisor: "المشرف",
  columnPhone: "الجوال",

  /* States */
  /* Every client still in service — نشطة وموقوفة معًا، باستثناء المؤرشفة. It is
     the default view, so the row switch never makes a row leave the screen. */
  statusAll: "الكل",
  statusActive: "نشطة",
  statusSuspended: "موقوفة مؤقتًا",
  statusArchived: "مؤرشفة",
  noLogin: "لا حساب دخول",
  /* Counts */
  supervisorsCount: "مشرفون",
  guardsCount: "حراس",
  zonesCount: "مواقع",

  /* Filters and search */
  searchPlaceholder: "ابحث بالاسم أو السجل التجاري…",
  filterState: "الحالة",
  filterLimits: "الحدود",
  filterContracts: "العقود",
  limitsNear: "قريبة من الحد",
  limitsFull: "بلغت الحد أو تجاوزته",
  contractsEnding: "ينتهي عقد خلال 70 يومًا",
  actionsLabel: "إجراءات",

  /* Actions */
  view: "عرض",
  edit: "تعديل بيانات الشركة",
  backToCompanies: "العودة إلى الشركات",
  backToCompany: "العودة إلى الشركة",
  suspend: "إيقاف مؤقت",
  reactivate: "إعادة التنشيط",
  archive: "أرشفة",
  restore: "استعادة",
  resendInvite: "إعادة إرسال رابط التنشيط",
  resetPassword: "إرسال رابط تعيين كلمة المرور",

  /* Confirmations. Each one says what happens to the client's access, because
     that is the part an admin is deciding and the part easiest to overlook. */
  suspendTitle: "إيقاف الشركة مؤقتًا",
  suspendBody:
    "لن تستطيع الشركة الدخول، وستُنتهى جلساتها الحالية. مشرفوها وحراسها يكملون عملهم بشكل عادي، وكل بياناتها تبقى كما هي، ويمكنك إعادة تنشيطها في أي وقت.",
  suspendConfirm: "إيقاف مؤقت",
  /* Said before the switch is flipped, not discovered after. The list defaults
     to the live clients, so a suspended one leaves the view — which looks like a
     deletion if nobody warned you. */
  suspendLeavesList: "الشركة هتختفي من قائمة الشركات النشطة، وتلاقيها في فلتر «موقوفة مؤقتًا».",
  reactivateTitle: "إعادة تنشيط الشركة",
  reactivateBody: "ستستطيع الشركة الدخول مرة أخرى بكلمة المرور نفسها.",
  reactivateConfirm: "إعادة التنشيط",
  archiveTitle: "أرشفة الشركة",
  archiveBody:
    "تُخرج الشركة من القوائم النشطة ويتوقف دخولها، ويصبح سجلها التجاري واسمها متاحين لشركة أخرى. البيانات التاريخية تبقى.",
  archiveConfirm: "أرشفة",
  restoreTitle: "استعادة الشركة",
  restoreBody: "تعود الشركة إلى القوائم النشطة. الدخول يظل موقوفًا حتى تُنشّطه.",
  restoreConfirm: "استعادة",
  resetPasswordTitle: "إرسال رابط تعيين كلمة المرور",
  resetPasswordBody:
    "سيصل بريد الشركة رابط تحدّد به كلمة مرور جديدة. كلمة مرورها الحالية تظل صالحة حتى تعيّن الجديدة، وأي رابط أُرسل قبل ذلك يتوقف. وعند تعيينها ستُنتهى جلساتها على كل الأجهزة.",
  resetPasswordConfirm: "إرسال الرابط",

  /* Outcomes */
  created: "تمت إضافة الشركة وإرسال رابط التنشيط على بريدها.",
  updated: "تم حفظ التعديلات.",

  /* Detail page */
  profileHeading: "بيانات الشركة",
  accessHeading: "حسابات الدخول",
  accessLead: "من يفتح لوحة العميل ويطّلع على حراسه ومواقعه.",
  addLogin: "إضافة حساب دخول",
  addLoginLead: "يصل إلى هذا البريد رابط لتعيين كلمة المرور، وتظهر الدعوة معلّقة حتى يُفعّلها صاحبها.",
  loginName: "اسم المسؤول لدى العميل",
  loginEmail: "البريد الإلكتروني",
  sendInvite: "إرسال الدعوة",
  pendingInvite: "دعوة معلّقة",
  addedOn: "تاريخ الإضافة",
  emptyValue: "—",
  noLimit: "بلا حد",
  viewTeam: "عرض الفريق",
  usageUnlimited: "مستخدم {used} · بلا حد",
  usageUnderLine: "مستخدم {used} · متبقٍ {remaining} (الحد {limit})",
  usageAtLine: "مستخدم {used} · متبقٍ {remaining} · بلغ الحد",
  usageOverLine: "مستخدم {used} · تجاوز بـ {over} (الحد {limit})",
  overBanner: "الشركة تتجاوز حدّ {kind} بـ {count} — الإضافات موقوفة حتى ينخفض العدد، ولم يُوقف أحد.",
  atBanner: "بلغت الشركة حدّ {kind} — الإضافات موقوفة حتى يتوفر مكان.",
  nearBanner: "الشركة قريبة من حدّ {kind}.",
  staffedSites: "{staffed} من {total} مكتملة الحراسة",
  assignedVsContracts: "المعيّنون / حسب العقود",
  mismatchShort: "لا يطابق العقود",
  matchShort: "يطابق العقود",
  guardsOnPlan: "الحراس على المخطط",
  sitesOnPlan: "مناطق الشركة على المخطط",
  planHint: "مخطط تقريبي لحدود المواقع. اسحب للتنقل، واستخدم عجلة الفأرة أو الأزرار للتكبير والتصغير.",
  siteStaffing: "{on} من {required}",
  teamHeading: "فريق الشركة",
  teamLead: "مشرفونا وحراسنا العاملون في مناطق هذا العميل.",
  teamSearch: "ابحث بالاسم أو المشرف أو الجوال",
  inactiveNotCounted: "{count} في القائمة بحساب موقوف — لا يُحسبون في العدد ولا ضمن الحد.",
  noGuardsTitle: "لا يوجد حراس على مناطق هذه الشركة",
  contractsHeading: "المواقع والعقود",
  contractsLead:
    "عقود ومواقع العميل. يمكن إضافة أكثر من عقد للشركة الواحدة. يُرسل تنبيه قبل انتهاء العقد بـ ٧٠ يومًا أو أقل.",
  analyticsHeading: "أداء الحراسة لدى العميل",
  analyticsLead: "ساعات العمل والانضباط في مواقع هذا العميل.",
  workedOfScheduled: "ساعات منجزة من المجدولة",
  workedOfScheduledValue: "{worked} من {scheduled}",
  noSupervisorsTitle: "لا يوجد مشرفون بعد",
  noSupervisorsLead: "عيّن مشرفًا على هذه الشركة من شاشة الموظفين، والحراس يتبعونه تلقائيًا.",
  assignSupervisor: "تعيين مشرف",
  noZonesTitle: "لا توجد مواقع بعد",
  noZonesLead: "أضف موقعًا وأسنده لهذه الشركة حتى تظهر عملياتها.",
  addZone: "إضافة موقع",
  guardsOfSupervisor: "حراسه",
  noContractsTitle: "لا توجد عقود بعد",
  noContractsLead: "أضف عقدًا أو موقعًا لهذه الشركة.",
  addContract: "إضافة عقد",
  editContract: "تعديل العقد",
  removeContract: "حذف العقد",
  removeContractTitle: "حذف العقد؟",
  removeContractBody: "سيُحذف هذا العقد نهائيًا من سجلات الشركة.",
  removeContractConfirm: "حذف",
  contractClientName: "اسم العميل / الشركة",
  contractClientNameHint: "قد يطابق اسم الشركة المسجّلة أو يكون جهة عميل مختلفة.",
  contractStartDate: "تاريخ بداية العقد",
  contractEndDate: "تاريخ نهاية العقد",
  contractEndDateHint: "يُنبَّه الفريق قبل الانتهاء بـ ٧٠ يومًا أو أقل، وبنفس نوافذ تنبيه عقود الموظفين.",
  contractValue: "قيمة العقد",
  contractedEmployees: "عدد الموظفين",
  contractedEmployeesHint: "عدد الحراس/الموظفين المتعاقد عليه لهذا الموقع.",
  contractContactName: "اسم جهة الاتصال المعتمدة (لدى العميل)",
  contractContactEmail: "بريد جهة الاتصال",
  contractContactMobile: "جوال جهة الاتصال",
  contractZone: "الموقع الجغرافي (اختياري)",
  contractZoneHint: "اربط العقد بمنطقة للشركة لمقارنة العدد المتعاقد مع الحراس المعيّنين عليها.",
  contractZoneNone: "بدون ربط بمنطقة",
  contractSaved: "تم حفظ العقد.",
  contractDeleted: "تم حذف العقد.",
  headcountStrip: "مقارنة عدد الموظفين",
  headcountContracted: "المتعاقد عليه",
  headcountAssigned: "المعيّن فعليًا",
  headcountMismatch: "يوجد اختلاف بين العدد المتعاقد والعدد المعيّن.",
  headcountMatch: "العدد المتعاقد يطابق المعيّنين على الشركة.",
  daysUntilExpiry: "متبقٍ",
  days: "يوم",
  expirySoon: "ينتهي قريبًا",
  expired: "منتهٍ",
  columnClient: "العميل",
  columnPeriod: "المدة",
  columnValue: "القيمة",
  columnHeadcount: "الموظفون",
  columnContact: "جهة الاتصال",
  columnExpiry: "الانتهاء",

  /* Analytics tiles. Same vocabulary as the reports screen, on purpose: the same
     number must not have two names. */
  rangeLabel: "المدة",
  range30: "آخر ٣٠ يومًا",
  range7: "آخر ٧ أيام",
  range90: "آخر ٩٠ يومًا",
  punctualityRate: "الالتزام بالمواعيد",
  lateMinutes: "دقائق التأخير",
  absences: "غياب",
  outsideZone: "بصمات خارج الموقع",
  unclosedPunches: "بصمات غير مغلقة",
  noAnalytics: "لا توجد بيانات في هذه المدة.",

  /* Empty states */
  emptyTitle: "لا توجد شركات بعد",
  emptyLead: "أضف أول شركة عميلة، وعيّن عليها مشرفًا وحراسًا.",
  emptyFilteredTitle: "لا نتائج مطابقة",
  emptyFilteredLead: "جرّب تعديل البحث أو تغيير الحالة.",
  clearFilters: "إزالة التصفية",

  /*
   * Validation messages.
   *
   * These seven only, because they are the ones the shared zod schema renders in
   * the browser before a request is ever made. Every other companies error — a
   * registration already taken, a name already used — is a uniqueness verdict
   * only the database can reach, so the API owns its wording and this catalogue
   * must not hold a second copy of it to drift.
   */
  errors: {
    nameTooShort: "اسم الشركة يجب أن يكون حرفين على الأقل.",
    nameTooLong: "اسم الشركة طويل جدًا.",
    crInvalid: "السجل التجاري يجب أن يكون ١٠ أرقام.",
    vatInvalid: "الرقم الضريبي يجب أن يكون ١٥ رقمًا، أو اتركه فارغًا.",
    ibanInvalid: "رقم الحساب غير صحيح. مثال: SA0380000000608010167519",
    notesTooLong: "الملاحظات طويلة جدًا.",
    emailInvalid: "البريد الإلكتروني غير صحيح.",
    clientNameRequired: "اسم العميل مطلوب.",
    clientNameTooLong: "اسم العميل طويل جدًا.",
    contractDatesRequired: "تاريخ البداية والنهاية مطلوبان.",
    contractDateInvalid: "صيغة التاريخ غير صحيحة.",
    contractEndBeforeStart: "تاريخ نهاية العقد يجب أن يكون بعد تاريخ البداية أو مساويًا له.",
    contractValueRequired: "قيمة العقد مطلوبة.",
    contractValueInvalid: "قيمة العقد غير صحيحة.",
    contractedCountRequired: "عدد الموظفين المتعاقد عليه مطلوب.",
    contractedCountInvalid: "عدد الموظفين يجب أن يكون رقمًا صحيحًا أكبر من صفر.",
    contactNameRequired: "اسم جهة الاتصال مطلوب.",
    contactEmailInvalid: "بريد جهة الاتصال غير صحيح.",
    contactMobileInvalid: "رقم جوال جهة الاتصال غير صحيح.",
    contractsJsonInvalid: "قائمة العقود غير صحيحة.",
  },
} as const;

/**
 * Weekday names.
 *
 * @dynamic — every read is a computed lookup (`weekdays[key]`) from the
 * heatmap's row axis, which the key checker cannot see. Its own export rather
 * than a nested object inside `reports`, because marking the whole reports group
 * dynamic would switch off the unused-key check for fifty real strings.
 */
export const weekdays = {
  sunday: "الأحد",
  monday: "الإثنين",
  tuesday: "الثلاثاء",
  wednesday: "الأربعاء",
  thursday: "الخميس",
  friday: "الجمعة",
  saturday: "السبت",
} as const;

/** The shared KPI tile: the comparison line under a figure. */
export const metrics = {
  /* Direction is carried by an icon AND this word, never by colour alone —
     --ok against --viz-5 is ΔE 4.1 apart under protanopia. */
  up: "ارتفاع",
  down: "انخفاض",
  flat: "بلا تغيير",
  vsPrevious: "مقارنة بالفترة السابقة",
  vsYesterday: "مقارنة بأمس",
  /* A rate that moves from ٨٠٪ to ٤٠٪ has fallen forty POINTS, not forty
     percent — the relative drop is fifty. Labelling the difference between two
     percentages as a percentage is the same species of quiet wrongness the
     reports module exists to have removed. */
  points: "نقطة مئوية",
} as const;

/** The frame every chart sits in. */
export const charts = {
  showTable: "عرض البيانات كجدول",
  hideTable: "إخفاء الجدول",
  tableCaption: "نفس بيانات الرسم، كجدول",
  empty: "لا بيانات في هذه الفترة.",
  /* Composed with fmt at the call site so the numerals follow the reader's
     setting rather than being baked into the string. */
  coverageOf: "أيام فيها بيانات من أصل",
  /* Named so a legend entry and a table row cannot disagree about the unit. */
  unitPunches: "عدد التسجيلات",
  unitPercent: "نسبة مئوية",
  unitHours: "ساعات",
  unitDays: "أيام",
  /* The disclosure table's own heads, shared by every chart that shows a
     per-day series. */
  tableDay: "اليوم",
  tableRate: "النسبة",
  tableHour: "الساعة",
  tableMetric: "البند",
} as const;

export const payrollRuns = {
  eyebrow: "الرواتب والدوام",
  title: "مسيّرات الرواتب",
  lead: "مسيّر لكل شهر: يُحسب لحظيًا وهو مسودة، ويُقفل عند الاعتماد. ثم يُسجَّل دفعه.",
  create: "إنشاء مسيّر",
  rules: "قواعد الرواتب",
  itemTypes: "أنواع الخصومات والمكافآت",
  month: "الشهر",
  status: "الحالة",
  employees: "الموظفون",
  gross: "الإجمالي",
  deductions: "الخصومات",
  net: "الصافي",
  review: "يحتاج مراجعة",
  draft: "مسودة",
  partial: "معتمد جزئيًا",
  approved: "معتمد",
  paid: "مدفوع",
  today: "اليوم",
  yesterday: "أمس",
  daysAgo: "قبل {n} أيام",
  back: "العودة إلى المسيّرات",
  print: "طباعة",
  approveRun: "اعتماد المسيّر",
  approveSelected: "اعتماد المحدد",
  payAll: "تسجيل دفع الكل",
  paySelected: "تسجيل دفع المحدد",
  exportFile: "ملف التحويل البنكي",
  banner: "سجلات دوام لم تُعتمد: {n}",
  approveReady: "اعتماد السجلات المتبقية",
  showReady: "عرض السجلات الجاهزة",
  approvedOf: "المعتمد",
  employee: "الموظف",
  overtime: "إضافي",
  bonuses: "مكافآت",
  payment: "الدفع",
  bank: "تحويل بنكي",
  cash: "نقدًا",
  cheque: "شيك",
  noIban: "بلا آيبان",
  paidOn: "دُفع",
  all: "عرض الكل",
  search: "ابحث بالاسم أو الرقم الوظيفي",
  company: "الشركة",
  empty: "لا موظفون في هذا العرض.",
  noRuns: "لا مسيّرات بعد.",
  issueAttendance: "سجل الدوام لم يُعتمد",
  issueOvertime: "وقت إضافي معلّق",
  issuePayment: "بلا آيبان ولا طريقة دفع",
  issueOpenShift: "أيام بلا انصراف",
  issueTask: "أجر بالمهمة — يُدخل يدويًا",
  issueEos: "ترك العمل — مكافأة نهاية الخدمة خارج النظام",
  issuePending: "خصومات أو مكافآت معلّقة",
  issueContract: "لا يوجد عقد",
  issueExpired: "العقد منتهٍ",
  issueCap: "خصم تجاوز الحد ودخل",
  issueNegative: "الصافي سالب",
  openRecord: "فتح السجل",
  overtimeDecision: "قرار الوقت الإضافي",
  addIban: "إضافة آيبان",
  openStatement: "فتح الكشف",
  openFile: "فتح الملف",
  openItems: "عرض البنود",
  addContract: "إضافة عقد",
  openContract: "فتح العقد",
  approveTitle: "اعتماد مسيّر الرواتب",
  approveBody: "تُقفل سجلات دوام الشهر وتُنشر كشوف الرواتب للموظفين في تطبيقاتهم. أي تغيير بعدها يُحتسب فرقًا في الشهر التالي.",
  approveConfirm: "اعتماد المسيّر",
  payTitle: "تسجيل دفع الرواتب",
  payBody: "يُسجَّل الدفع لكل من له طريقة دفع محددة. من لا آيبان له ولم تُحدد طريقة دفعه يبقى معلّقًا.",
  payConfirm: "تسجيل دفع الكل",
  cancel: "إلغاء",
  skipped: "لم يُعتمد",
  createTitle: "إنشاء مسيّر شهر",
  createBody: "يضم كل موظف له عقد في الشهر، ويُحسب من سجلات الدوام والخصومات والمكافآت المعتمدة.",
  createMonth: "الشهر",
  optional: "(اختياري)",
  submitCreate: "إنشاء",
  reopen: "إعادة فتح",
  reopenTitle: "إعادة فتح الكشف",
  reopenBody: "يعود الكشف إلى المسودة ويُعاد حسابه. اكتب السبب؛ يُحفظ في سجل التغييرات.",
  reopenReason: "السبب",
  reopenSubmit: "إعادة الفتح",
  overtimeTitle: "قرار الوقت الإضافي",
  overtimeBody: "اعتماد الدقائق يبقيها في الأجر. الرفض يُسقطها من هذا الشهر.",
  overtimeApprove: "اعتماد الوقت الإضافي",
  overtimeReject: "رفض الوقت الإضافي",
  rulesTitle: "قواعد الرواتب",
  rulesLead: "تُطبَّق القواعد الفعّالة تلقائيًا عند حساب المسودة. المسيّر المعتمد يحتفظ بنسخة القواعد التي اعتُمد بها.",
  ruleName: "اسم القاعدة",
  ruleFrom: "سارية من",
  ruleActive: "مفعّلة",
  ruleSave: "حفظ القاعدة",
  ruleGrace: "فترة السماح (دقائق)",
  ruleRate: "المبلغ لكل دقيقة (ر.س)",
  rulePercent: "النسبة ٪",
  ruleMultiplier: "معامل الوقت الإضافي",
  ruleHours: "ساعات اليوم",
  ruleBasis: "أساس الحساب",
  basisBasic: "الأساسي",
  basisWage: "الأساسي والسكن والنقل",
  ruleCap: "حد الخصم من الإجمالي ٪",
  itemsTitle: "أنواع الخصومات والمكافآت",
  itemsLead: "أسماء البنود اليدوية. المبلغ والسبب يبقيان على كشف الموظف.",
  itemName: "اسم البند",
  itemKind: "النوع",
  itemAdd: "إضافة نوع",
  itemSave: "حفظ",
  kindDeduction: "خصم",
  kindBonus: "مكافأة",
  kindProration: "احتساب جزئي لمن التحق أو غادر خلال الشهر",
  kindLateness: "تأخير",
  kindEarlyLeave: "انصراف مبكر",
  kindAbsence: "غياب",
  kindOutOfZone: "خروج عن النطاق",
  kindBreak: "تجاوز وقت الاستراحة",
  kindOvertime: "وقت إضافي",
  kindGosi: "التأمينات الاجتماعية",
  kindCap: "حد الخصم",
  saved: "تم الحفظ.",
  exported: "تم تنزيل ملف التحويل.",
  exportBlocked: "استُبعد بعض الموظفين من الملف.",
  warningNet: "تنبيه",
} as const;
